import axios, { type AxiosRequestConfig } from 'axios';
import { getStoredCompanyDataScope } from '../utils/companyDataScope';
import { getStoredStoreDataScope } from '../utils/storeDataScope';

// Criação da instância Axios apontando para a API Node.js Local ou VPS
// ⚠️ Em produção, a URL deve ser relativa ou definida via ENV
// Para desenvolvimento local, pode ser http://localhost:3000
const rawApiUrl = String(import.meta.env.VITE_API_URL || '').trim();
const API_URL = rawApiUrl.replace(/\/api\/?$/, ''); // As rotas já incluem /api/... no path
type ApiJsonRecord = Record<string, unknown>;
type ApiJsonList = ApiJsonRecord[];
type PublicSaasPlanRecord = ApiJsonRecord & {
    id: string;
    name: string;
    price: number;
    interval: string;
    maxUsers?: number | null;
    subcompanyConfigured?: boolean;
    subcompanyLimit?: number | null;
    modulesConfig?: Record<string, boolean>;
    modularPricing?: Record<string, number>;
    features?: string[];
    active?: boolean;
    __fallback?: boolean;
};
type CrmClientsResponse = CRMClient[] | { data: CRMClient[]; total: number; page: number; totalPages: number };
type DealListFilters = ApiJsonRecord & {
    status?: string;
    bank?: string;
    promoter?: string;
    agentId?: string;
    startDate?: string;
    endDate?: string;
    storeId?: string;
};
type DealListResponse = ApiJsonRecord | Deal[];
type AdminCompanyRecord = ApiJsonRecord;
type AdminPaymentRecord = ApiJsonRecord;
type AdminSaasPlanRecord = ApiJsonRecord;
type AdminSaasPlanPayload = ApiJsonRecord;
type AdminCompanyPayload = ApiJsonRecord & {
    name: string;
    email?: string;
    cpfCnpj?: string;
    phone?: string;
    ownerName?: string;
    ownerEmail?: string;
    ownerPassword?: string;
    planId: string;
    status?: 'active' | 'blocked' | 'pending_payment';
    vertical?: string;
    modules?: Record<string, boolean>;
    subscriptionConfig?: ApiJsonRecord;
};
export type PrivacyRequestType =
    | 'confirmation'
    | 'access'
    | 'correction'
    | 'anonymization'
    | 'deletion'
    | 'portability'
    | 'consent_revocation'
    | 'marketing_opt_out'
    | 'automated_decision_review'
    | 'incident_question'
    | 'other';
export type PrivacyRequestStatus =
    | 'open'
    | 'identity_pending'
    | 'in_progress'
    | 'waiting_customer'
    | 'completed'
    | 'rejected'
    | 'cancelled';
export type PrivacyRequestRecord = ApiJsonRecord & {
    id: string;
    company_id: string;
    client_id?: string | null;
    request_type: PrivacyRequestType;
    status: PrivacyRequestStatus;
    subject_name?: string | null;
    subject_document?: string | null;
    subject_phone?: string | null;
    subject_email?: string | null;
    identity_verified: boolean;
    source?: string | null;
    description?: string | null;
    resolution_notes?: string | null;
    requested_by?: string | null;
    assigned_to?: string | null;
    completed_by?: string | null;
    due_at?: string | null;
    completed_at?: string | null;
    metadata_jsonb?: ApiJsonRecord;
    created_at: string;
    updated_at?: string | null;
};
export type PrivacyRequestPayload = {
    requestType: PrivacyRequestType;
    subjectName?: string;
    subjectDocument?: string;
    subjectPhone?: string;
    subjectEmail?: string;
    clientId?: string;
    source?: string;
    description?: string;
    identityVerified?: boolean;
    assignedTo?: string;
    metadata?: ApiJsonRecord;
};
export type PublicPrivacyCompanyInfo = {
    companyId: string;
    companyName: string;
    requestTypes: Array<{ value: PrivacyRequestType; label: string }>;
};
export type PublicPrivacyRequestPayload = {
    requestType: PrivacyRequestType;
    subjectName?: string;
    subjectDocument?: string;
    subjectPhone?: string;
    subjectEmail?: string;
    description?: string;
    identityDeclarationAccepted: boolean;
};
export type PublicPrivacyRequestResponse = {
    accepted: boolean;
    companyName: string;
    request: Pick<PrivacyRequestRecord, 'id' | 'company_id' | 'request_type' | 'status' | 'due_at' | 'created_at'>;
    message: string;
};
export type PublicSelfServiceCatalogOption = {
    id: string;
    name: string;
    description?: string;
    providerKey?: string;
    productKeys?: string[];
    fgtsAuthorizationTargets?: string[];
};
export type PublicSelfServiceCompanyInfo = {
    companyId: string;
    companyName: string;
    slug: string;
    headline: string;
    description: string;
    primaryColor: string;
    logoUrl?: string;
    faviconUrl?: string;
    metaPixelId?: string;
    whatsappNumber?: string;
    products: PublicSelfServiceCatalogOption[];
    banks: PublicSelfServiceCatalogOption[];
    consentText: string;
};
export type PublicSelfServiceLeadPayload = {
    name: string;
    cpf: string;
    phone: string;
    email?: string;
    productId?: string;
    bankId?: string;
    providerKeys?: string[];
    consentAccepted: boolean;
};
export type PublicSelfServiceSimulationOffer = {
    providerKey: string;
    providerName: string;
    authorizationTarget?: string | null;
    simulationId?: string | null;
    requestedAmount: number;
    installmentAmount: number;
    installments: number;
    tableName?: string;
};
export type PublicSelfServiceSimulationProviderResult = {
    providerKey: string;
    providerName: string;
    authorizationTarget?: string | null;
    authorizationKind?: 'fgts_app' | 'bank_consent' | 'link' | null;
    consultId?: string | null;
    authorizationLink?: string | null;
    status: 'simulated' | 'authorization_required' | 'processing' | 'unavailable' | 'temporary_error' | 'error';
    offers: PublicSelfServiceSimulationOffer[];
};
export type PublicSelfServiceSimulationResponse = {
    status: 'simulated' | 'authorization_required' | 'processing' | 'unavailable' | 'error';
    productId?: string | null;
    productName?: string | null;
    submissionId?: string | null;
    bestOffer?: PublicSelfServiceSimulationOffer | null;
    offers: PublicSelfServiceSimulationOffer[];
    authorizationProviders: Array<{
        providerKey: string;
        providerName: string;
        authorizationTarget?: string | null;
        authorizationKind?: 'fgts_app' | 'bank_consent' | 'link' | null;
        authorizationLink?: string | null;
        displayName: string;
    }>;
    providerResults: PublicSelfServiceSimulationProviderResult[];
    message: string;
    generatedAt: string;
};
export type PublicSelfServiceLeadResponse = {
    accepted: boolean;
    companyName: string;
    client: {
        id: string;
        name: string;
    };
    submission: {
        id: string;
        created_at: string;
    };
    simulation?: PublicSelfServiceSimulationResponse | null;
    message: string;
};
export type PublicSelfServiceSettings = {
    enabled: boolean;
    slug: string;
    headline: string;
    description: string;
    metaPixelId: string;
    whatsappNumber: string;
    productKeys: string[];
    providerKeys: string[];
    publicPath: string;
};
export type PrivacyRequestUpdatePayload = {
    status?: PrivacyRequestStatus;
    identityVerified?: boolean;
    description?: string;
    resolutionNotes?: string;
    assignedTo?: string;
    metadata?: ApiJsonRecord;
};
export type PrivacyExportPayload = ApiJsonRecord & {
    generatedAt: string;
    companyId: string;
    request: ApiJsonRecord;
    identifiers: ApiJsonRecord;
    data: ApiJsonRecord;
};
export type PrivacyAnonymizationPlan = ApiJsonRecord & {
    generatedAt: string;
    requestId: string;
    includeMessages: boolean;
    canApply: boolean;
    warnings: string[];
    counts: {
        clients?: number;
        deals?: number;
        whatsappContacts?: number;
        whatsappTickets?: number;
        whatsappMessages?: number;
        mailingLeads?: number;
        aiRecords?: number;
        bankingConsents?: number;
        enrichmentConsults?: number;
        [key: string]: number | undefined;
    };
    matchedClients?: Array<{
        id: string;
        name?: string | null;
        cpf?: string | null;
        phone?: string | null;
        email?: string | null;
    }>;
};
export type PrivacyAnonymizationResult = ApiJsonRecord & {
    accepted: boolean;
    request: PrivacyRequestRecord;
    counts: Record<string, number>;
    warnings?: string[];
};
export type PrivacyConsentPurpose =
    | 'marketing'
    | 'whatsapp_service'
    | 'proposal_follow_up'
    | 'banking_simulation'
    | 'data_enrichment'
    | 'ai_assistance'
    | 'portfolio_reactivation'
    | 'other';
export type PrivacyConsentStatus = 'active' | 'revoked' | 'expired' | 'blocked';
export type PrivacyLegalBasis =
    | 'consent'
    | 'contract'
    | 'legal_obligation'
    | 'credit_protection'
    | 'legitimate_interest'
    | 'regular_exercise'
    | 'other';
export type PrivacyConsentRecord = ApiJsonRecord & {
    id: string;
    company_id: string;
    client_id?: string | null;
    subject_name?: string | null;
    subject_document?: string | null;
    subject_phone?: string | null;
    subject_email?: string | null;
    purpose: PrivacyConsentPurpose;
    channel: string;
    legal_basis: PrivacyLegalBasis;
    status: PrivacyConsentStatus;
    source?: string | null;
    evidence_text?: string | null;
    evidence_url?: string | null;
    consented_at?: string | null;
    revoked_at?: string | null;
    expires_at?: string | null;
    created_at: string;
    updated_at?: string | null;
};
export type PrivacyConsentPayload = {
    clientId?: string;
    subjectName?: string;
    subjectDocument?: string;
    subjectPhone?: string;
    subjectEmail?: string;
    purpose?: PrivacyConsentPurpose;
    channel?: string;
    legalBasis?: PrivacyLegalBasis;
    status?: PrivacyConsentStatus;
    source?: string;
    evidenceText?: string;
    evidenceUrl?: string;
    consentedAt?: string;
    expiresAt?: string;
    metadata?: ApiJsonRecord;
};
export type PrivacyRetentionAction = 'review' | 'anonymize' | 'delete' | 'archive';
export type PrivacyRetentionPolicy = ApiJsonRecord & {
    id: string;
    company_id: string;
    category: string;
    label: string;
    description?: string | null;
    retention_days: number;
    action: PrivacyRetentionAction;
    legal_basis: PrivacyLegalBasis;
    enabled: boolean;
    notes?: string | null;
    updated_at?: string | null;
};
export type PrivacyRetentionPreviewItem = ApiJsonRecord & {
    policyId: string;
    category: string;
    label: string;
    description?: string | null;
    retentionDays: number;
    action: PrivacyRetentionAction;
    legalBasis: PrivacyLegalBasis;
    enabled: boolean;
    cutoffAt: string;
    candidateCount: number;
    oldestRecordAt?: string | null;
};
export type PrivacyRetentionPreview = ApiJsonRecord & {
    generatedAt: string;
    policies: PrivacyRetentionPreviewItem[];
    totalCandidates: number;
};
type InteractionApiRecord = ApiJsonRecord & {
    id?: string | number;
    type?: string;
    content?: string;
    created_at?: string;
    author_name?: string;
    channel?: string;
    result?: string;
    metadata?: unknown;
    metadata_jsonb?: unknown;
};
type SupportTicketApiRecord = ApiJsonRecord & {
    id?: string | number;
    subject?: string;
    status?: string;
    priority?: string;
    created_at?: string;
    updated_at?: string;
    company_id?: string | number;
    user_id?: string | number;
    user_name?: string;
    user_email?: string;
    category_name?: string;
    category?: string;
    company_name?: string;
    description?: string;
    sla_deadline?: string | null;
    assignee_id?: string | number | null;
};
type WhatsAppOnlineAgentApiRecord = ApiJsonRecord & {
    user_id?: string | number;
    id?: string | number;
    name?: string;
    active_sessions?: string | number;
    last_seen_at?: string | null;
};
export type WhatsAppTicketRecord = ApiJsonRecord & {
    id: string;
    contact_name: string;
    contact_phone: string;
    profile_pic_url: string;
    channel?: 'whatsapp' | 'instagram' | 'messenger' | string | null;
    client_id?: string | null;
    deal_id?: string | null;
    instance_id?: number | null;
    instance_name?: string | null;
    instance_phone?: string | null;
    queue_id?: number | null;
    queue_name?: string | null;
    queue_color?: string | null;
    phase?: string | null;
    tags?: Array<{ id: number; name: string; color?: string | null }>;
    status: 'unassigned' | 'open' | 'resolved';
    created_at?: string | null;
    last_message_at: string;
    last_customer_message_at?: string | null;
    last_agent_message_at?: string | null;
    last_message_preview: string;
    unread_count: number;
    waiting_since?: string | null;
    assignee_id?: string | null;
    assignee_name?: string;
    signature_enabled?: boolean;
    conversation_mode?: 'human' | 'bot' | 'awaiting_human';
    bot_rule_id?: number | null;
    handoff_requested_at?: string | null;
    handoff_reason?: string | null;
    snoozed_until?: string | null;
    snooze_reason?: string | null;
    snooze_reason_code?: string | null;
    snoozed_by?: string | null;
    sla_deadline?: string | null;
    sla_warning_at?: string | null;
    sla_state?: 'ok' | 'warning' | 'overdue' | null;
    resolution_reason?: string | null;
    resolution_notes?: string | null;
    reopened_at?: string | null;
    reopened_by?: string | null;
    reopen_reason?: string | null;
    reopen_notes?: string | null;
    resolved_at?: string | null;
    csat_recent_status?: 'pending' | 'answered' | null;
    csat_recent_sent_at?: string | null;
    csat_recent_responded_at?: string | null;
    csat_recent_rating?: number | null;
    csat_recent_feedback_text?: string | null;
};
type StoreScopedFields = {
    storeId?: string | null;
    store_id?: string | null;
    companyId?: string | null;
    company_id?: string | null;
};

export const api = axios.create({
    baseURL: API_URL,
});

export const PUBLIC_SAAS_CONFIG_FALLBACK = {
    trialEnabled: true,
    trialDays: 7
};

export const PUBLIC_SAAS_PLANS_FALLBACK: PublicSaasPlanRecord[] = [
    {
        id: 'crm-operacional',
        name: 'CRM Operacional',
        price: 297,
        interval: 'monthly',
        maxUsers: 10,
        subcompanyConfigured: true,
        subcompanyLimit: 0,
        modulesConfig: {
            marketing: false,
            whatsapp: false,
            whiteLabel: false,
            apiAccess: false,
            bankingIntegrations: false,
            bankAccess: true,
            dataEnrichment: false,
            portability: false,
            whatsappAi: false
        },
        features: [
            'Clientes, carteira e vendas no mesmo lugar',
            'Retornos, tarefas e rotina comercial organizados',
            'Gestão financeira, comissões e repasses',
            'Relatórios e visão da operação'
        ],
        active: true,
        __fallback: true
    },
    {
        id: 'crm-whatsapp',
        name: 'CRM + WhatsApp',
        price: 497,
        interval: 'monthly',
        maxUsers: 10,
        subcompanyConfigured: true,
        subcompanyLimit: 0,
        modulesConfig: {
            marketing: true,
            whatsapp: true,
            whiteLabel: false,
            apiAccess: false,
            bankingIntegrations: false,
            bankAccess: true,
            dataEnrichment: false,
            portability: false,
            whatsappAi: false
        },
        features: [
            'Tudo do CRM Operacional',
            'Atendimento por WhatsApp com equipe e filas',
            'Menu de triagem, respostas rápidas e contexto do CRM',
            'Disparo em massa para API WhatsApp e WhatsApp Web'
        ],
        active: true,
        __fallback: true
    },
    {
        id: 'crm-rede',
        name: 'CRM Rede',
        price: 697,
        interval: 'monthly',
        maxUsers: 10,
        subcompanyConfigured: true,
        subcompanyLimit: 1,
        modulesConfig: {
            marketing: true,
            whatsapp: true,
            whiteLabel: false,
            apiAccess: false,
            bankingIntegrations: false,
            bankAccess: true,
            dataEnrichment: false,
            portability: false,
            whatsappAi: false
        },
        features: [
            'Tudo do CRM + WhatsApp',
            '1 Filial inclusa com login e operação separados',
            'Visão consolidada da rede com filtros por unidade',
            'Repasse e gestão operacional por filial'
        ],
        active: true,
        __fallback: true
    }
];

export type IntegrationFieldDefinition = {
    key: string;
    label: string;
    type: 'text' | 'password' | 'url';
    placeholder?: string;
    helperText?: string;
    required?: boolean;
};

export type IntegrationCatalogRecord = {
    id: string;
    providerKey: string;
    name: string;
    category: string;
    segment?: string;
    summary: string;
    description: string;
    implementationStatus: string;
    active: boolean;
    managedByCode?: boolean;
    capabilities?: Record<string, boolean>;
    fgtsAuthorizationTargets?: string[];
    fgtsOfferTables?: string[];
    fields: IntegrationFieldDefinition[];
    createdAt?: string | null;
    updatedAt?: string | null;
};

export type CompanyIntegrationRecord = {
    id: string;
    providerKey: string;
    providerName: string;
    category: string;
    enabled: boolean;
    status: 'draft' | 'configured' | 'active' | 'error';
    credentials: Record<string, string>;
    configuredFields?: Record<string, boolean>;
    secretsRedacted?: boolean;
    lastValidatedAt?: string | null;
    lastError?: string;
    updatedAt?: string | null;
    createdAt?: string | null;
};

export type CompanySmsConnectionResponse = {
    success: boolean;
    providerKey: string;
    providerName: string;
    enabled: boolean;
    status: CompanyIntegrationRecord['status'];
    balance?: number | null;
    message: string;
};

export type CompanySmsSendResponse = {
    success: boolean;
    providerKey: string;
    providerName: string;
    clientId?: string | null;
    clientName?: string | null;
    dealId?: string | null;
    interactionId?: string | null;
    receiver?: string | null;
    sender?: string | null;
    requestUniqueId?: string | null;
    scheduledFor?: string | null;
    message: string;
    warning?: string | null;
};

export type CompanyWhatsappMetaProviderSettings = {
    provider: 'techprovider';
    oauthEnabled: boolean;
    instagramEnabled?: boolean;
    messengerEnabled?: boolean;
    oauthUrl: string;
    launchUrl: string;
    coexistenceEnabled?: boolean;
    coexistenceLaunchUrl?: string;
    coexistenceConfigId?: string;
    instagramWebhookUrl: string;
    messengerWebhookUrl: string;
    instagramPageId?: string;
    instagramPageName?: string;
    instagramAccountId?: string;
    instagramUsername?: string;
    instagramName?: string;
    messengerPageId?: string;
    source?: string;
    mode?: string;
    clientId?: string;
    configId?: string;
    instagramClientId?: string;
    instagramConfigId?: string;
    instagramLaunchUrl?: string;
    webhookSecret?: string;
    hasWebhookSecret?: boolean;
};

export type ApiDocsRouteRecord = {
    method: string;
    path: string;
    section: string;
    moduleGroup: string;
    summary: string;
    authRequired: boolean;
    permissions: string[];
    modules: string[];
    allowedRoles: string[];
    tags: string[];
    requestExample: string;
    responseExample: string;
    audience: 'customer' | 'internal' | string;
    sourceFile?: string;
    line?: number;
};

export type ApiDocsCatalogResponse = {
    generatedAt: string;
    totalRoutes: number;
    visibleRoutes: number;
    hiddenRoutes: number;
    viewer: {
        role: string;
        permissions: string[];
        explicitModules: string[];
        effectiveModules: Record<string, boolean>;
    };
    routes: ApiDocsRouteRecord[];
};

export type CrmAssistantScope = 'dashboard' | 'clients' | 'client-detail' | 'whatsapp' | 'intelligence-hub' | 'generic';
export type CrmAssistantIntent = 'overview' | 'next-step' | 'reply' | 'task' | 'risk-check';

export type CrmAssistantSuggestedTask = {
    title: string;
    description?: string | null;
    priority?: 'low' | 'medium' | 'high';
    dueDate?: string | null;
    clientId?: string | null;
};

export type CrmAssistantRecommendation = {
    id: string;
    kind: 'insight' | 'task' | 'reply' | 'crm';
    label: string;
    description: string;
};

export type CrmAssistantSectionItem = {
    label: string;
    value: string;
    detail?: string;
};

export type CrmAssistantSection = {
    id: string;
    title: string;
    tone?: 'neutral' | 'opportunity' | 'warning' | 'action' | 'data';
    items: CrmAssistantSectionItem[];
};

export type CrmAssistantContextPayload = {
    companyName?: string;
    companyId?: string;
    currentUserName?: string;
    viewLabel?: string;
    mission?: 'report' | 'daily-plan' | 'reactivation' | 'campaign' | 'seller-analysis' | 'screen-help';
    prompt?: string;
    helpTargetView?: string;
    helpTargetLabel?: string;
    searchTerm?: string;
    topSegments?: string[];
    segmentInsights?: Array<{ name?: string; count?: number }>;
    topProducts?: Array<{ name?: string; value?: number; count?: number }>;
    topUsers?: Array<{
        id?: string;
        name?: string;
        paidValue?: number;
        openValue?: number;
        dealsCount?: number;
        paidDealsCount?: number;
        conversionRate?: number;
        goal?: number;
        goalGap?: number;
        goalProgress?: number;
    }>;
    reactivationSegments?: Array<{ name?: string; count?: number }>;
    mailingSegments?: Array<{ name?: string; count?: number }>;
    staleOpenByProduct?: Array<{ name?: string; count?: number }>;
    audiences?: Array<{
        label?: string;
        size?: number;
        rationale?: string;
    }>;
    metrics?: {
        paidMonthValue?: number;
        openPipelineValue?: number;
        openDealsCount?: number;
        staleOpenDealsCount?: number;
        clientsCount?: number;
        filteredClientsCount?: number;
        pendingTasksCount?: number;
        dealsCount?: number;
        cancelledValue?: number;
        monthGoal?: number;
        projectionValue?: number;
        unattendedTicketsCount?: number;
        mailingLeadsCount?: number;
        clientsWithoutDealsCount?: number;
        sellersCount?: number;
        activeSellersCount?: number;
        sellersWithoutProductionCount?: number;
        sellersBelowGoalCount?: number;
    };
    client?: {
        id?: string;
        name?: string;
        cpf?: string;
        phone?: string;
        email?: string;
        segment?: string;
        segment_name?: string;
        orgao?: string;
        productTags?: string[];
        city?: string;
        state?: string;
    };
    crmClient?: {
        id?: string;
        name?: string;
        cpf?: string;
        phone?: string;
        email?: string;
        segment?: string;
        segment_name?: string;
        orgao?: string;
        productTags?: string[];
        product_tags?: string[];
    };
    ticket?: {
        id?: string;
        contactName?: string;
        contactPhone?: string;
        queueName?: string;
        phase?: string;
        status?: string;
        unreadCount?: number;
        assigneeName?: string;
        assigneeId?: string;
        dealId?: string | null;
        conversationMode?: string;
    };
    deals?: Array<{
        id?: string;
        product?: string;
        bank?: string;
        status?: string;
        value?: number;
        updatedAt?: string;
        createdAt?: string;
    }>;
    messages?: Array<{
        id?: string;
        direction?: string;
        content?: string;
        type?: string;
        createdAt?: string;
    }>;
    recentSales?: Array<{
        clientName?: string;
        product?: string;
        bank?: string;
        value?: number;
        status?: string;
    }>;
};

export type CrmAssistantRequest = {
    scope: CrmAssistantScope;
    intent?: CrmAssistantIntent;
    context: CrmAssistantContextPayload;
};

export type CrmAssistantResponse = {
    scope: CrmAssistantScope;
    intent: CrmAssistantIntent;
    generatedAt: string;
    provider: string;
    contextLabel: string;
    headline: string;
    summary: string;
    bullets: string[];
    sections?: CrmAssistantSection[];
    recommendations: CrmAssistantRecommendation[];
    suggestedReply?: string | null;
    suggestedTask?: CrmAssistantSuggestedTask | null;
};

export type AdminWhatsAppMarketingBlacklistEntry = {
    id: number;
    phone_normalized: string;
    raw_phone?: string | null;
    blocked_reason?: string | null;
    source?: string | null;
    import_batch_id?: string | null;
    imported_by?: string | null;
    imported_by_name?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
};

export type AdminWhatsAppMarketingBlacklistImportRun = {
    id: string;
    file_name: string;
    import_mode: 'merge' | 'replace';
    fallback_reason?: string | null;
    status: 'pending' | 'running' | 'completed' | 'failed';
    total_rows_processed: number;
    valid_rows: number;
    skipped_rows: number;
    inserted_count: number;
    updated_count: number;
    error_message?: string | null;
    import_batch_id?: string | null;
    created_by?: string | null;
    created_by_name?: string | null;
    started_at?: string | null;
    finished_at?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
};

export type AdminWhatsAppMarketingBlacklistSummary = {
    total: number;
    items: AdminWhatsAppMarketingBlacklistEntry[];
    message?: string;
    accepted?: boolean;
    importRun?: AdminWhatsAppMarketingBlacklistImportRun | null;
    latestImportRun?: AdminWhatsAppMarketingBlacklistImportRun | null;
    importSummary?: {
        mode: 'merge' | 'replace';
        batchId: string;
        importedCount: number;
        insertedCount: number;
        updatedCount: number;
    };
};

export type CrmWorkflowRequirementField = {
    code: string;
    label: string;
};

export type CrmWorkflowProfile = {
    id: string;
    name: string;
    track: string;
    bank?: string;
    product?: string;
    requiredBeforeTypingRequest: string[];
    requiredBeforeProposalNumber: string[];
    requiredBeforeContractLink: string[];
    typingSlaMinutes: {
        low: number;
        medium: number;
        high: number;
    };
};

export type CrmWorkflowSettingsResponse = {
    profiles: CrmWorkflowProfile[];
    availableTracks: string[];
    availableRequirementFields: CrmWorkflowRequirementField[];
    message?: string;
};

export type AdminSecurityEventSeverity = 'critical' | 'error' | 'warning' | 'info';

export type AdminSecurityEvent = {
    id: string;
    company_id?: string | null;
    actor_user_id?: string | null;
    severity: AdminSecurityEventSeverity | string;
    event_type: string;
    source?: string | null;
    message: string;
    metadata_jsonb?: ApiJsonRecord | null;
    created_at?: string | null;
};

export type AdminSecurityEventListResponse = {
    total: number;
    items: AdminSecurityEvent[];
    filters: {
        companyId?: string | null;
        severity?: string | null;
        eventType?: string | null;
        source?: string | null;
        limit: number;
    };
};

export type EnrichmentConsultSummary = {
    document: string;
    name?: string;
    motherName?: string;
    birthDate?: string;
    score?: string;
    estimatedIncome?: string;
    pep?: boolean;
    providerMessage?: string;
    hasStructuredData?: boolean;
    resultStatus?: 'has_data' | 'no_data';
    lastCompany?: {
        name?: string | null;
        document?: string | null;
    } | null;
    phones?: string[];
    emails?: string[];
    addresses?: string[];
    extraFields?: Record<string, string>;
    cacheMeta?: {
        scope?: 'company' | 'global' | 'provider';
        consultedAt?: string | null;
        sourceConsultId?: string | null;
    };
    normalizedClientData?: {
        name?: string;
        cpf?: string;
        phone?: string;
        email?: string;
        birthDate?: string;
        gender?: string;
        rg?: string;
        motherName?: string;
        zipCode?: string;
        street?: string;
        number?: string;
        neighborhood?: string;
        city?: string;
        state?: string;
        complement?: string;
    };
    relatedPeopleCount?: number;
};

export type EnrichmentConsultRecord = {
    id: string;
    providerKey: string;
    providerName: string;
    clientId?: string | null;
    clientName?: string;
    document: string;
    summary: EnrichmentConsultSummary;
    raw: unknown;
    createdBy?: string | null;
    createdAt?: string | null;
};

export type ProviderIntegrationStatus = {
    providerKey: string;
    providerName: string;
    catalogActive: boolean;
    implemented: boolean;
    capabilities: Record<string, boolean>;
    configured: boolean;
    enabled: boolean;
    status: string;
};

export type SecureBrowserBookmark = {
    id: string;
    label: string;
    url: string;
    description?: string;
};

export type SecureBrowserSettings = {
    enabled: boolean;
    launchUrlTemplate: string;
    homeUrl: string;
    bookmarks: SecureBrowserBookmark[];
};

export type NovoSaqueFgtsSimulationResult = {
    tableName: string;
    productId: string;
    balanceSource: string;
    simulationId?: string | null;
    liquidValue: number;
    liquidValueInCents: number;
    requestedAmount: number;
    requestedAmountInCents: number;
    initialValue: number;
    initialValueInCents: number;
    installments: number;
    firstPaymentDate?: string | null;
    firstInstallmentValue: number;
    monthlyInterest: number;
    yearlyInterest: number;
    raw: unknown;
};

export type BankingOperationRecord = {
    id: string;
    providerKey: string;
    providerName: string;
    clientId?: string | null;
    clientName: string;
    clientCpf?: string;
    dealId?: string | null;
    dealProposalNumber?: string | null;
    dealStatus?: string | null;
    dealUpdatedAt?: string | null;
    crmProduct?: string;
    crmBankId?: string | null;
    crmBankName?: string;
    crmPromoterId?: string | null;
    crmPromoterName?: string;
    crmTableId?: string | null;
    crmTableName?: string;
    status: string;
    statusLabel: string;
    simulationServiceId?: string | null;
    bankCustomerId?: string | null;
    bankContractId?: string | null;
    onboardingLink?: string;
    selectedSimulation?: unknown;
    customerSnapshot?: ApiJsonRecord;
    providerResponse?: ApiJsonRecord;
    lastError?: string;
    createdBy?: string | null;
    updatedBy?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
};

export type BankingConsentRecord = {
    id: string;
    providerKey: string;
    providerName: string;
    clientId?: string | null;
    clientName: string;
    clientCpf?: string;
    sellerUserId?: string | null;
    sellerName?: string;
    crmProduct?: string;
    crmBankId?: string | null;
    crmBankName?: string;
    crmPromoterId?: string | null;
    crmPromoterName?: string;
    crmTableId?: string | null;
    crmTableName?: string;
    consultId: string;
    authorizationLink?: string;
    status: string;
    statusLabel: string;
    statusCode?: number | null;
    requiresLinkSelection?: boolean;
    selectedLinkId?: string;
    linkedOperationId?: string | null;
    linkedDealId?: string | null;
    dealStatus?: string | null;
    dealUpdatedAt?: string | null;
    consultSnapshot?: ApiJsonRecord;
    simulationSnapshot?: ApiJsonRecord;
    customerSnapshot?: ApiJsonRecord;
    flowState?: ApiJsonRecord;
    providerResponse?: ApiJsonRecord;
    lastError?: string;
    createdBy?: string | null;
    updatedBy?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
};

export type BankingSimulationCacheRecord = {
    id: string;
    segment: string;
    providerKey: string;
    providerName: string;
    clientId?: string | null;
    clientName: string;
    clientCpf?: string;
    crmContext?: {
        productName?: string;
        bankId?: string | null;
        bankName?: string;
        promoterId?: string | null;
        promoterName?: string;
        tableId?: string | null;
        tableName?: string;
    };
    requestSnapshot?: ApiJsonRecord;
    responseSnapshot?: ApiJsonRecord;
    customerSnapshot?: ApiJsonRecord;
    uiSnapshot?: ApiJsonRecord;
    lastError?: string;
    createdBy?: string | null;
    updatedBy?: string | null;
    lastSimulatedAt?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
};

export type BankingHygieneHistoryRecord = {
    id: string;
    segment: string;
    providerKey: string;
    providerName: string;
    source: 'clients' | 'mailing' | 'file' | string;
    sourceId?: string | null;
    clientId?: string | null;
    clientName: string;
    clientCpf?: string;
    clientPhone?: string;
    clientEmail?: string;
    status: string;
    statusLabel?: string;
    offerValue?: number | null;
    installments?: number | null;
    tableName?: string;
    simulationId?: string | null;
    offerCount?: number | null;
    message?: string;
    requestSnapshot?: ApiJsonRecord;
    responseSnapshot?: ApiJsonRecord;
    customerSnapshot?: ApiJsonRecord;
    uiSnapshot?: ApiJsonRecord;
    lastError?: string;
    createdBy?: string | null;
    createdAt?: string | null;
};

export type CltConsultRecord = {
    id: string;
    partnerId?: string;
    documentNumber?: string;
    availableMarginValue?: string;
    status?: string;
    statusCode?: number | null;
    statusDescription?: string | null;
    description?: string;
    name?: string;
    authorizationLink?: string | null;
    expiresAt?: string | null;
    rawStatus?: string | null;
    requiresLinkSelection?: boolean;
    vinculos?: ApiJsonRecord[];
    selectedLinkId?: string;
};

export type AiPromptProfile = {
    id: string;
    key: string;
    name: string;
    description?: string;
    systemPrompt: string;
    supportedProducts: string[];
    isActive: boolean;
    sortOrder: number;
};

export type AiProviderConfig = {
    providerKey: string;
    providerName: string;
    fgtsAuthorizationTargets: string[];
    fgtsOfferTables: string[];
};

export type CompanyWhatsAppAiProviderSetting = {
    providerKey: string;
    fgtsAuthorizationTarget: string;
    fgtsOfferTableOrder: string[];
    fgtsOfferStrategy?: 'manual_order' | 'highest_customer_value' | 'highest_estimated_commission' | 'balanced';
};

export type CompanyWhatsAppAiProductProviderPriority = {
    fgts: string;
    clt: string;
    consignado: string;
    portabilidade: string;
    cartao: string;
};

export type AdminAiSettings = {
    enabled: boolean;
    provider: 'hybrid' | 'openai' | 'ollama' | 'heuristic';
    openai: {
        enabled: boolean;
        baseUrl: string;
        model: string;
        miniModel: string;
        nanoModel: string;
        temperature: number;
        maxOutputTokens: number;
        apiKeyConfigured: boolean;
        apiKey?: string;
        clearApiKey?: boolean;
    };
    ollama: {
        enabled: boolean;
        baseUrl: string;
        model: string;
        temperature: number;
        maxOutputTokens: number;
    };
    routing: {
        cacheEnabled: boolean;
        cacheTtlSeconds: number;
        lowCostProvider: 'ollama' | 'openai_nano' | 'openai_mini';
        highValueProvider: 'ollama' | 'openai_nano' | 'openai_mini';
    };
    promptProfiles: AiPromptProfile[];
    providerConfigs: AiProviderConfig[];
};

export type CompanyWhatsAppAiNumberPolicy = {
    instanceId: number;
    enabled: boolean;
    agentName: string;
    promptProfileId: string;
    autopilotScope: 'qualify_only' | 'simulate_only' | 'simulate_and_register' | 'full';
    allowedProviderKeys: string[];
    preferredProviderByProduct: CompanyWhatsAppAiProductProviderPriority;
    providerSettings: CompanyWhatsAppAiProviderSetting[];
};

export type CompanyWhatsAppAiSettings = {
    enabled: boolean;
    humanFallbackEnabled: boolean;
    numbers: CompanyWhatsAppAiNumberPolicy[];
};

export type CompanyWhatsAppAiSettingsResponse = {
    settings: CompanyWhatsAppAiSettings;
    promptProfiles: AiPromptProfile[];
    scopes: Array<'qualify_only' | 'simulate_only' | 'simulate_and_register' | 'full'>;
    providerConfigs: AiProviderConfig[];
};

export type WhatsAppAiReviewStatus = 'pending' | 'approved' | 'rejected';

export type WhatsAppAiReviewItem = {
    id: string;
    companyId: string;
    companyName: string;
    ticketId: string;
    messageId?: string | null;
    instanceId?: number | null;
    instanceName: string;
    contactName: string;
    contactPhone: string;
    clientName: string;
    mode: string;
    autopilotScope: string;
    promptProfileId: string;
    model: string;
    normalizedInput: string;
    replyText: string;
    currentProduct: string;
    currentProviderKey: string;
    runStatus: string;
    errorText: string;
    createdAt: string;
    reviewStatus: WhatsAppAiReviewStatus;
    reviewComment: string;
    reviewSuggestedReply: string;
    reviewedAt?: string | null;
    reviewerName: string;
};

export type WhatsAppAiReviewMessage = {
    id: string;
    direction: 'inbound' | 'outbound' | string;
    type: string;
    content: string;
    status: string;
    createdAt: string;
    metadata?: Record<string, unknown>;
};

export type WhatsAppAiReviewDetail = WhatsAppAiReviewItem & {
    ticketStatus: string;
    ticketPhase: string;
    conversationMode: string;
    memorySummary: string;
    memoryStage: string;
    memoryIntent: string;
    messages: WhatsAppAiReviewMessage[];
};

export type WhatsAppAiReviewListResponse = {
    items: WhatsAppAiReviewItem[];
    stats: {
        total: number;
        pending: number;
        approved: number;
        rejected: number;
    };
};

export type LegacyAiExampleStatus = 'candidate' | 'review' | 'approved' | 'rejected' | 'ignored';

export type LegacyAiExampleItem = {
    id: string;
    sourceSystem: string;
    sourceLabel: string;
    legacyTenantId?: number | null;
    legacyTicketId?: number | null;
    legacyMessageId: string;
    channel: string;
    ticketStatus: string;
    queueId?: number | null;
    productHint: string;
    intentHint: string;
    outcomeHint: string;
    qualityScore: number;
    occurrenceCount: number;
    trainingStatus: LegacyAiExampleStatus;
    estimatedTokens: number;
    contextText: string;
    replyText: string;
    reviewComment: string;
    reviewSuggestedReply: string;
    reviewedAt?: string | null;
    reviewerName: string;
    firstSeenAt?: string | null;
    lastSeenAt?: string | null;
    createdAt: string;
    importRunId?: string | null;
};

export type LegacyAiCompactMessage = {
    role: string;
    text: string;
};

export type LegacyAiExampleDetail = LegacyAiExampleItem & {
    compactMessages: LegacyAiCompactMessage[];
    metadata?: Record<string, unknown>;
};

export type LegacyAiExampleListResponse = {
    items: LegacyAiExampleItem[];
    stats: {
        total: number;
        candidate: number;
        review: number;
        approved: number;
        rejected: number;
        ignored: number;
    };
};

// Interceptor para injetar o Token em cada requisição
api.interceptors.request.use(async (config) => {
    try {
        const localToken = localStorage.getItem('token');

        if (localToken && !config.headers?.Authorization) {
            config.headers.Authorization = `Bearer ${localToken}`;
        }

        const requestUrl = String(config.url || '').trim();
        const shouldIgnoreDataScope = !requestUrl
            || requestUrl.startsWith('/api/auth/')
            || requestUrl.startsWith('/api/public/')
            || requestUrl.startsWith('/api/admin/')
            || requestUrl.startsWith('/api/system/')
            || requestUrl === '/api/crm/network/context';

        const requestHeaders = (config.headers || {}) as ApiJsonRecord;
        const hasExplicitCompanyView = Boolean(requestHeaders[COMPANY_VIEW_HEADER]);

        if (!shouldIgnoreDataScope && !hasExplicitCompanyView) {
            const storedScope = getStoredCompanyDataScope();
            const requestMethod = String(config.method || 'get').trim().toUpperCase();
            const existingManagedCompanyId = String(requestHeaders[MANAGED_COMPANY_HEADER] || '').trim();

            if (storedScope.mode === 'subcompany' && storedScope.companyId && !existingManagedCompanyId) {
                config.headers = {
                    ...(config.headers || {}),
                    'X-Managed-Company-Id': storedScope.companyId
                };
            }

            if (requestMethod === 'GET') {
                const scopeParam = storedScope.mode === 'network'
                    ? 'network'
                    : 'own';
                const storedStoreScope = getStoredStoreDataScope();
                const activeCompanyId = String(storedScope.companyId || '').trim()
                    || String(existingManagedCompanyId || '').trim()
                    || '';

                const currentParams = (typeof config.params === 'object' && config.params !== null ? config.params : {}) as ApiJsonRecord;
                config.params = {
                    ...currentParams,
                    companyScope: currentParams.companyScope || scopeParam,
                    ...(storedStoreScope.mode === 'store'
                        && storedStoreScope.storeId
                        && storedStoreScope.companyId
                        && storedStoreScope.companyId === activeCompanyId
                        && !currentParams.storeId
                        ? { storeId: storedStoreScope.storeId }
                        : {})
                };
            }
        }
    } catch (error) {
        console.error("Erro ao injetar token:", error);
    }
    return config;
});

// Interceptor para tratar respostas, especialmente 401 (Unauthorized)
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            const requestUrl = error.config?.url || '';
            // Evitar loop infinito ou redirecionamento ao simplesmente errar a senha no login
            if (!requestUrl.includes('/api/auth/login')) {
                console.warn('Recebido 401 Unauthorized. Emitindo evento auth:unauthorized');
                window.dispatchEvent(new Event('auth:unauthorized'));
            }
        }
        return Promise.reject(error);
    }
);

const COMPANY_VIEW_HEADER = 'X-Company-View-Id';
const MANAGED_COMPANY_HEADER = 'X-Managed-Company-Id';

const buildCompanyViewConfig = <T extends AxiosRequestConfig>(config: T = {} as T, explicitCompanyId?: string | null): T => {
    const selectedCompanyId = String(explicitCompanyId || '').trim();

    if (!selectedCompanyId) {
        return config;
    }

    return {
        ...config,
        headers: {
            ...(config.headers || {}),
            [COMPANY_VIEW_HEADER]: selectedCompanyId
        }
    };
};

const buildManagedCompanyConfig = <T extends AxiosRequestConfig>(config: T = {} as T, explicitCompanyId?: string | null): T => {
    const managedCompanyId = String(explicitCompanyId || '').trim();

    if (!managedCompanyId) {
        return config;
    }

    return {
        ...config,
        headers: {
            ...(config.headers || {}),
            [MANAGED_COMPANY_HEADER]: managedCompanyId
        }
    };
};

const applyStoreScopeToPayload = <T extends object>(payload: T, explicitStoreId?: string | null): T & StoreScopedFields => {
    const nextPayload = { ...payload } as T & StoreScopedFields;
    const directStoreId = String(explicitStoreId || '').trim();

    if (directStoreId) {
        if (nextPayload.store_id === undefined) nextPayload.store_id = directStoreId;
        if (nextPayload.storeId === undefined) nextPayload.storeId = directStoreId;
        return nextPayload;
    }

    const storedScope = getStoredStoreDataScope();
    if (storedScope.mode === 'store' && storedScope.storeId) {
        if (nextPayload.store_id === undefined) nextPayload.store_id = storedScope.storeId;
        if (nextPayload.storeId === undefined) nextPayload.storeId = storedScope.storeId;
    }

    return nextPayload;
};

// Tipagens para o TypeScript não reclamar
import type { CRMClient, Deal, Interaction, ApiResponse, CreateDealDTO, User, CreateUserDTO, UpdateUserDTO, SalesAgentDTO, Bank, Promoter, Segment, Product, Ticket, TicketMessage, CommissionTable, MailingLead } from '../types';
import type {
    CompanyNetworkContext,
    CompanyStoreDetail,
    CompanyStoresContext,
    UsefulLink,
    WhatsAppTemplate
} from '../modules/finance/financeTypes';

type CrmClientApiPayload = Partial<CRMClient> & StoreScopedFields & ApiJsonRecord;
type DealPayloadExtras = StoreScopedFields & {
    importBatchId?: string | null;
    nextFollowUp?: string | null;
    interactions?: unknown;
    commissionStatus?: string | null;
    commissionReceivedDate?: string | null;
};

export interface WhatsAppClientOperationalSummary {
    client_id: string;
    ticket_id: string;
    contact_phone?: string | null;
    status: 'open' | 'unassigned' | 'resolved';
    queue_id?: number | null;
    queue_name?: string | null;
    queue_color?: string | null;
    phase?: string | null;
    assignee_id?: string | null;
    assignee_name?: string | null;
    unread_count?: number;
    last_message_at?: string | null;
    last_message_preview?: string | null;
    last_customer_message_at?: string | null;
    last_agent_message_at?: string | null;
    sla_deadline?: string | null;
    sla_warning_at?: string | null;
    sla_state?: 'ok' | 'warning' | 'overdue' | null;
}

export interface ClientDuplicatePreviewClient {
    id: string;
    name: string;
    cpf?: string | null;
    phone?: string | null;
    email?: string | null;
    score: number;
    dealCount: number;
    interactionCount: number;
    ticketCount: number;
    contactCount: number;
    taskCount: number;
    updatedAt?: string | null;
    createdAt?: string | null;
}

export interface ClientDuplicateGroupPreview {
    keyType: 'cpf' | 'phone';
    key: string;
    reason: string;
    reasonLabel: string;
    target: ClientDuplicatePreviewClient | null;
    sources: ClientDuplicatePreviewClient[];
    clientCount: number;
    mergedSourceCount?: number;
    movedRecords?: Record<string, number>;
}

export interface ClientDuplicatePreviewResponse {
    summary: {
        totalClients: number;
        duplicateGroups: number;
        safeGroups: number;
        reviewGroups: number;
        mergeableClients: number;
        clientsInReviewGroups: number;
    };
    safeGroups: ClientDuplicateGroupPreview[];
    reviewGroups: ClientDuplicateGroupPreview[];
}

export interface ClientDuplicateMergeResponse {
    message: string;
    mergedGroups: number;
    mergedClients: number;
    mergedSamples: ClientDuplicateGroupPreview[];
    remainingSummary: ClientDuplicatePreviewResponse['summary'];
    remainingReviewGroups: ClientDuplicateGroupPreview[];
}

export interface DealThermometerSignal {
    id: string;
    client_id: string;
    status?: string | null;
    loss_reason?: string | null;
    cancellation_reason?: string | null;
    next_follow_up?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface WhatsAppOnlineAgent {
    id: string;
    name: string;
    active_sessions: number;
    last_seen_at?: string | null;
    is_online: boolean;
}

export interface WhatsAppMediaLibraryItem {
    id: string;
    file_id: string;
    title: string;
    description?: string | null;
    category?: string | null;
    queue_id?: number | null;
    queue_name?: string | null;
    is_active?: boolean;
    sort_order?: number;
    file_name?: string | null;
    file_url?: string | null;
    mime_type?: string | null;
    file_size?: number | null;
    created_by?: string | null;
    created_by_name?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface WhatsAppConversationMediaItem {
    id: string;
    ticket_id: string;
    direction: 'inbound' | 'outbound';
    type?: string | null;
    content?: string | null;
    created_at?: string | null;
    ticket_status?: string | null;
    ticket_phase?: string | null;
    queue_id?: number | null;
    queue_name?: string | null;
    queue_color?: string | null;
    instance_id?: number | null;
    instance_name?: string | null;
    contact_name?: string | null;
    contact_phone?: string | null;
    sender_name?: string | null;
    file_id: string;
    file_name?: string | null;
    file_url?: string | null;
    mime_type?: string | null;
    file_size?: number | null;
}

export type WhatsAppInboxTab = 'queue' | 'mine' | 'resolved' | 'snoozed';
export type WhatsAppInboxMode = 'human' | 'ai';

export interface WhatsAppInboxSavedView {
    id: string;
    name: string;
    inboxMode?: WhatsAppInboxMode;
    activeTab: WhatsAppInboxTab;
    queueFilterId: string;
    ticketSearchTerm: string;
    createdAt?: string | null;
    updatedAt?: string | null;
}

export interface WhatsAppInboxPreferences {
    currentState: {
        inboxMode?: WhatsAppInboxMode;
        activeTab: WhatsAppInboxTab;
        queueFilterId: string;
        ticketSearchTerm: string;
        activeSavedViewId: string | null;
    };
    savedViews: WhatsAppInboxSavedView[];
    signatureEnabled?: boolean | null;
    signaturePolicy?: {
        requireAgentSignatureForNonAdmins?: boolean;
        requiredForNonAdmins?: boolean;
        locked?: boolean;
        canToggle?: boolean;
    } | null;
}

export interface InternalChatDirectoryUser {
    userId: string;
    name: string;
    email?: string | null;
    role?: string | null;
    isActive: boolean;
}

export interface InternalChatParticipant {
    userId: string;
    name: string;
    role?: string | null;
    isActive: boolean;
}

export interface InternalChatAttachment {
    url: string;
    name: string;
    mimeType?: string | null;
    size?: number | null;
}

export interface InternalChatConversation {
    id: string;
    type: 'group' | 'direct' | 'team';
    title?: string | null;
    conversationKey: string;
    accentColor?: string | null;
    lastMessageAt?: string | null;
    lastMessagePreview?: string | null;
    updatedAt?: string | null;
    unreadCount: number;
    participants: InternalChatParticipant[];
}

export interface InternalChatMessage {
    id: string;
    conversationId: string;
    senderUserId: string;
    senderName: string;
    content: string;
    createdAt: string;
    updatedAt?: string | null;
    isMine: boolean;
    attachment?: InternalChatAttachment | null;
}

export interface InternalChatBootstrap {
    canManageTeams: boolean;
    users: InternalChatDirectoryUser[];
    conversations: InternalChatConversation[];
    defaultConversationId?: string | null;
    initialMessages?: InternalChatMessage[];
}

// Tipagens (Movidas para src/types/index.ts)
// export interface CRMClient { ... }
// export interface Deal { ... }

const mapCrmClientPayload = (raw: CrmClientApiPayload): CRMClient => ({
    id: raw?.id,
    name: raw?.name,
    cpf: raw?.cpf,
    phone: raw?.phone,
    email: raw?.email,
    company_id: raw?.company_id,
    store_id: raw?.store_id,
    store_name: raw?.store_name,
    storeId: raw?.storeId || raw?.store_id,
    storeName: raw?.storeName || raw?.store_name,
    agent_id: raw?.agent_id,
    created_at: raw?.created_at,
    segment: raw?.segment,
    segment_name: raw?.segment_name,
    matricula: raw?.matricula,
    orgao: raw?.orgao,
    product_tags: raw?.product_tags,
    productTags: raw?.productTags || raw?.product_tags,
    enrichment_extra_data: raw?.enrichment_extra_data || raw?.enrichment_extra_data_jsonb || {},
    enrichmentExtraData: raw?.enrichmentExtraData || raw?.enrichment_extra_data || raw?.enrichment_extra_data_jsonb || {},
    birthDate: raw?.birth_date,
    gender: raw?.gender,
    rg: raw?.rg,
    motherName: raw?.mother_name,
    zipCode: raw?.zip_code,
    street: raw?.street,
    number: raw?.number,
    neighborhood: raw?.neighborhood,
    city: raw?.city,
    state: raw?.state,
    complement: raw?.complement,
    pixKey: raw?.pix_key,
    bank: raw?.bank,
    agency: raw?.agency,
    account: raw?.account,
    accountType: raw?.account_type
} as CRMClient);

export const crmService = {
    getPublicSaasConfig: async (): Promise<{ trialEnabled: boolean, trialDays: number, metaPixelId?: string }> => {
        try {
            const res = await api.get('/api/public/saas/config');
            return res.data;
        } catch {
            return { ...PUBLIC_SAAS_CONFIG_FALLBACK };
        }
    },

    getPublicSaasPlans: async (): Promise<PublicSaasPlanRecord[]> => {
        try {
            const res = await api.get('/api/public/saas/plans');
            return res.data;
        } catch {
            return PUBLIC_SAAS_PLANS_FALLBACK.map((plan) => ({ ...plan }));
        }
    },

    getPublicPrivacyCompany: async (empresaSlug: string): Promise<PublicPrivacyCompanyInfo> => {
        const res = await api.get<PublicPrivacyCompanyInfo>(`/api/public/privacy/${encodeURIComponent(empresaSlug)}`);
        return res.data;
    },

    createPublicPrivacyRequest: async (
        empresaSlug: string,
        payload: PublicPrivacyRequestPayload
    ): Promise<PublicPrivacyRequestResponse> => {
        const res = await api.post<PublicPrivacyRequestResponse>(
            `/api/public/privacy/${encodeURIComponent(empresaSlug)}/requests`,
            payload
        );
        return res.data;
    },

    getPublicSelfServiceCompany: async (empresaSlug: string): Promise<PublicSelfServiceCompanyInfo> => {
        const res = await api.get<PublicSelfServiceCompanyInfo>(`/api/public/self-service/${encodeURIComponent(empresaSlug)}`);
        return res.data;
    },

    createPublicSelfServiceLead: async (
        empresaSlug: string,
        payload: PublicSelfServiceLeadPayload
    ): Promise<PublicSelfServiceLeadResponse> => {
        const res = await api.post<PublicSelfServiceLeadResponse>(
            `/api/public/self-service/${encodeURIComponent(empresaSlug)}/leads`,
            payload
        );
        return res.data;
    },

    getApiDocsCatalog: async (): Promise<ApiDocsCatalogResponse> => {
        const res = await api.get<ApiDocsCatalogResponse>('/api/system/api-docs/catalog');
        return res.data;
    },

    // Usuários
    getUsers: async (): Promise<User[]> => {
        const res = await api.get<User[]>('/api/crm/users', buildCompanyViewConfig());
        return res.data;
    },

    updateUser: async (id: string, data: UpdateUserDTO): Promise<User> => {
        const res = await api.put<User>(`/api/crm/users/${id}`, data);
        return res.data;
    },

    deleteUser: async (id: string): Promise<void> => {
        await api.delete(`/api/crm/users/${id}`);
    },

    createUser: async (data: CreateUserDTO): Promise<User> => {
        const res = await api.post<User>('/api/crm/users', data);
        return res.data;
    },

    createSalesAgent: async (data: SalesAgentDTO): Promise<User> => {
        const res = await api.post<User>('/api/crm/users/sales-agents', data);
        return res.data;
    },

    updateSalesAgent: async (id: string, data: Partial<SalesAgentDTO>): Promise<User> => {
        const res = await api.put<User>(`/api/crm/users/sales-agents/${id}`, data);
        return res.data;
    },

    deleteSalesAgent: async (id: string): Promise<void> => {
        await api.delete(`/api/crm/users/sales-agents/${id}`);
    },

    listPrivacyRequests: async (params?: {
        status?: PrivacyRequestStatus | 'all';
        type?: PrivacyRequestType | 'all';
        q?: string;
        limit?: number;
    }): Promise<PrivacyRequestRecord[]> => {
        const normalizedParams = {
            ...params,
            status: params?.status === 'all' ? undefined : params?.status,
            type: params?.type === 'all' ? undefined : params?.type
        };
        const res = await api.get<PrivacyRequestRecord[]>('/api/crm/privacy/requests', buildCompanyViewConfig({ params: normalizedParams }));
        return res.data;
    },

    createPrivacyRequest: async (payload: PrivacyRequestPayload): Promise<PrivacyRequestRecord> => {
        const res = await api.post<PrivacyRequestRecord>('/api/crm/privacy/requests', payload, buildCompanyViewConfig());
        return res.data;
    },

    updatePrivacyRequest: async (id: string, payload: PrivacyRequestUpdatePayload): Promise<PrivacyRequestRecord> => {
        const res = await api.put<PrivacyRequestRecord>(`/api/crm/privacy/requests/${id}`, payload, buildCompanyViewConfig());
        return res.data;
    },

    verifyPrivacyRequestIdentity: async (id: string, payload?: { method?: string; notes?: string }): Promise<PrivacyRequestRecord> => {
        const res = await api.post<PrivacyRequestRecord>(`/api/crm/privacy/requests/${id}/verify-identity`, payload || {}, buildCompanyViewConfig());
        return res.data;
    },

    exportPrivacyRequestData: async (id: string): Promise<PrivacyExportPayload> => {
        const res = await api.get<PrivacyExportPayload>(`/api/crm/privacy/requests/${id}/export`, buildCompanyViewConfig());
        return res.data;
    },

    previewPrivacyRequestAnonymization: async (id: string, payload?: { includeMessages?: boolean }): Promise<PrivacyAnonymizationPlan> => {
        const res = await api.post<PrivacyAnonymizationPlan>(`/api/crm/privacy/requests/${id}/anonymization-preview`, payload || {}, buildCompanyViewConfig());
        return res.data;
    },

    anonymizePrivacyRequestData: async (id: string, payload?: { includeMessages?: boolean; resolutionNotes?: string }): Promise<PrivacyAnonymizationResult> => {
        const res = await api.post<PrivacyAnonymizationResult>(`/api/crm/privacy/requests/${id}/anonymize`, payload || {}, buildCompanyViewConfig());
        return res.data;
    },

    completePrivacyRequest: async (id: string, payload?: { resolutionNotes?: string }): Promise<PrivacyRequestRecord> => {
        const res = await api.post<PrivacyRequestRecord>(`/api/crm/privacy/requests/${id}/complete`, payload || {}, buildCompanyViewConfig());
        return res.data;
    },

    listPrivacyConsents: async (params?: {
        status?: PrivacyConsentStatus | 'all';
        purpose?: PrivacyConsentPurpose | 'all';
        q?: string;
        limit?: number;
    }): Promise<PrivacyConsentRecord[]> => {
        const normalizedParams = {
            ...params,
            status: params?.status === 'all' ? undefined : params?.status,
            purpose: params?.purpose === 'all' ? undefined : params?.purpose
        };
        const res = await api.get<PrivacyConsentRecord[]>('/api/crm/privacy/consents', buildCompanyViewConfig({ params: normalizedParams }));
        return res.data;
    },

    createPrivacyConsent: async (payload: PrivacyConsentPayload): Promise<PrivacyConsentRecord> => {
        const res = await api.post<PrivacyConsentRecord>('/api/crm/privacy/consents', payload, buildCompanyViewConfig());
        return res.data;
    },

    revokePrivacyConsent: async (id: string, payload?: { reason?: string }): Promise<PrivacyConsentRecord> => {
        const res = await api.post<PrivacyConsentRecord>(`/api/crm/privacy/consents/${id}/revoke`, payload || {}, buildCompanyViewConfig());
        return res.data;
    },

    listPrivacyRetentionPolicies: async (): Promise<PrivacyRetentionPolicy[]> => {
        const res = await api.get<PrivacyRetentionPolicy[]>('/api/crm/privacy/retention-policies', buildCompanyViewConfig());
        return res.data;
    },

    updatePrivacyRetentionPolicy: async (id: string, payload: {
        retentionDays?: number;
        action?: PrivacyRetentionAction;
        legalBasis?: PrivacyLegalBasis;
        enabled?: boolean;
        notes?: string;
    }): Promise<PrivacyRetentionPolicy> => {
        const res = await api.put<PrivacyRetentionPolicy>(`/api/crm/privacy/retention-policies/${id}`, payload, buildCompanyViewConfig());
        return res.data;
    },

    previewPrivacyRetention: async (): Promise<PrivacyRetentionPreview> => {
        const res = await api.get<PrivacyRetentionPreview>('/api/crm/privacy/retention-preview', buildCompanyViewConfig());
        return res.data;
    },

    getInternalChatBootstrap: async (): Promise<InternalChatBootstrap> => {
        const res = await api.get<InternalChatBootstrap>('/api/crm/internal-chat/bootstrap');
        return res.data;
    },

    createInternalDirectConversation: async (userId: string): Promise<InternalChatConversation> => {
        const res = await api.post<InternalChatConversation>('/api/crm/internal-chat/conversations/direct', { userId });
        return res.data;
    },

    createInternalTeamConversation: async (payload: {
        title: string;
        color?: string;
        userIds: string[];
    }): Promise<InternalChatConversation> => {
        const res = await api.post<InternalChatConversation>('/api/crm/internal-chat/conversations/team', payload);
        return res.data;
    },

    updateInternalTeamConversation: async (conversationId: string, payload: {
        title?: string;
        color?: string;
        userIds: string[];
    }): Promise<InternalChatConversation> => {
        const res = await api.put<InternalChatConversation>(`/api/crm/internal-chat/conversations/${conversationId}/team`, payload);
        return res.data;
    },

    getInternalChatMessages: async (conversationId: string, limit = 80): Promise<InternalChatMessage[]> => {
        const res = await api.get<InternalChatMessage[]>(`/api/crm/internal-chat/conversations/${conversationId}/messages`, {
            params: { limit }
        });
        return res.data;
    },

    markInternalChatConversationRead: async (conversationId: string): Promise<{ success: boolean; conversation: InternalChatConversation | null }> => {
        const res = await api.put<{ success: boolean; conversation: InternalChatConversation | null }>(`/api/crm/internal-chat/conversations/${conversationId}/read`);
        return res.data;
    },

    sendInternalChatMessage: async (conversationId: string, payload: {
        content?: string;
        attachmentFileId?: string;
        attachmentName?: string;
        attachmentMimeType?: string;
        attachmentSize?: number;
    }): Promise<InternalChatMessage> => {
        const res = await api.post<InternalChatMessage>(`/api/crm/internal-chat/conversations/${conversationId}/messages`, payload);
        return res.data;
    },

    getInternalChatAttachmentBlob: async (messageId: string): Promise<Blob> => {
        const res = await api.get(`/api/crm/internal-chat/messages/${messageId}/attachment`, {
            responseType: 'blob'
        });

        return res.data;
    },

    // Clientes
    getClientById: async (id: string, options?: { companyId?: string | null }): Promise<CRMClient> => {
        const res = await api.get<CRMClient>(`/api/crm/clients/${id}`, buildManagedCompanyConfig(buildCompanyViewConfig(), options?.companyId));
        return mapCrmClientPayload(res.data);
    },

    findClientByPhone: async (phone: string): Promise<CRMClient | null> => {
        const normalizedPhone = String(phone || '').trim();
        if (!normalizedPhone) {
            return null;
        }

        const res = await api.get<CRMClient | null>('/api/crm/clients/lookup/by-phone', buildCompanyViewConfig({
            params: { phone: normalizedPhone }
        }));

        return res.data ? mapCrmClientPayload(res.data) : null;
    },

    getClients: async (
        pageOrFilters: number | {
            page?: number;
            search?: string;
            limit?: number;
            ids?: string[];
            segment?: string;
            orgao?: string;
            productTag?: string;
            governmentScope?: 'all' | 'government' | 'prefeitura';
            compact?: boolean;
            storeId?: string;
        } = 1,
        search = '',
        limit = 100
    ): Promise<CrmClientsResponse> => {
        const filters = typeof pageOrFilters === 'object'
            ? pageOrFilters
            : {
                page: pageOrFilters,
                search,
                limit
            };

        const res = await api.get('/api/crm/clients', buildCompanyViewConfig({
            params: {
                page: filters.page ?? 1,
                search: filters.search ?? '',
                limit: filters.limit ?? 100,
                ...(Array.isArray(filters.ids) && filters.ids.length > 0 ? { ids: filters.ids.join(',') } : {}),
                ...(filters.segment ? { segment: filters.segment } : {}),
                ...(filters.orgao ? { orgao: filters.orgao } : {}),
                ...(filters.productTag ? { productTag: filters.productTag } : {}),
                ...(filters.governmentScope && filters.governmentScope !== 'all'
                    ? { governmentScope: filters.governmentScope }
                    : {}),
                ...(filters.compact ? { compact: 'true' } : {}),
                ...(filters.storeId ? { storeId: filters.storeId } : {})
            }
        }));
        if (Array.isArray(res.data)) {
            return res.data.map(mapCrmClientPayload);
        }
        if (Array.isArray(res.data?.data)) {
            return {
                ...res.data,
                data: res.data.data.map(mapCrmClientPayload)
            };
        }
        return res.data;
    },

    createClient: async (data: CRMClient, options?: { companyId?: string | null }): Promise<CRMClient> => {
        const clientPayload = data as CRMClient & StoreScopedFields;
        const payload = applyStoreScopeToPayload(clientPayload, clientPayload.storeId ?? clientPayload.store_id);
        const managedCompanyId = options?.companyId ?? payload.companyId ?? payload.company_id ?? null;
        const res = await api.post<CRMClient>('/api/crm/clients', payload, buildManagedCompanyConfig({}, managedCompanyId));
        return mapCrmClientPayload(res.data);
    },

    mergeClients: async (sourceClientId: string, targetClientId: string, options?: { companyId?: string | null }): Promise<{
        message: string;
        sourceClientId: string;
        targetClientId: string;
        movedRecords?: Record<string, number>;
        client?: CRMClient;
    }> => {
        const res = await api.post(`/api/crm/clients/${sourceClientId}/merge`, {
            targetClientId
        }, buildManagedCompanyConfig({}, options?.companyId));
        return res.data;
    },

    previewClientDuplicates: async (sampleSize = 10): Promise<ClientDuplicatePreviewResponse> => {
        const res = await api.get('/api/crm/clients/duplicates/preview', {
            params: { sampleSize }
        });
        return res.data;
    },

    mergeSafeClientDuplicates: async (sampleSize = 10): Promise<ClientDuplicateMergeResponse> => {
        const res = await api.post('/api/crm/clients/duplicates/merge-safe', {
            sampleSize
        });
        return res.data;
    },

    checkCpf: async (cpf: string, options?: { companyId?: string | null }): Promise<{ exists: boolean, client?: CRMClient }> => {
        const res = await api.get(`/api/crm/clients/check-cpf/${cpf.replace(/\D/g, '')}`, buildManagedCompanyConfig({}, options?.companyId));
        if (res.data.exists && res.data.client) {
            res.data.client = mapCrmClientPayload(res.data.client);
        }
        return res.data;
    },

    updateClient: async (id: string, data: Partial<CRMClient>, options?: { companyId?: string | null }): Promise<CRMClient> => {
        const payload = applyStoreScopeToPayload(data as Partial<CRMClient> & StoreScopedFields);
        const res = await api.put<CRMClient>(`/api/crm/clients/${id}`, payload, buildManagedCompanyConfig({}, options?.companyId));
        return mapCrmClientPayload(res.data);
    },

    getClientInteractions: async (id: string, options?: { companyId?: string | null }): Promise<ApiJsonList> => {
        const res = await api.get(`/api/crm/clients/${id}/interactions`, buildManagedCompanyConfig(buildCompanyViewConfig(), options?.companyId));
        return res.data;
    },

    getClientDeals: async (id: string, options?: { companyId?: string | null }): Promise<Deal[]> => {
        const res = await api.get(`/api/crm/clients/${id}/deals`, buildManagedCompanyConfig(buildCompanyViewConfig(), options?.companyId));
        return res.data;
    },

    getWhatsAppClientSummaries: async (clientIds: string[]): Promise<WhatsAppClientOperationalSummary[]> => {
        const normalizedIds = Array.from(new Set((clientIds || []).map((value) => String(value || '').trim()).filter(Boolean)));
        if (normalizedIds.length === 0) {
            return [];
        }
        const query = new URLSearchParams();
        query.set('client_ids', normalizedIds.join(','));
        const res = await api.get(`/api/crm/chat/client-summaries?${query.toString()}`, buildCompanyViewConfig());
        return res.data;
    },

    getDealThermometerSignals: async (clientIds: string[]): Promise<DealThermometerSignal[]> => {
        const normalizedIds = Array.from(new Set((clientIds || []).map((value) => String(value || '').trim()).filter(Boolean)));
        if (normalizedIds.length === 0) {
            return [];
        }
        const query = new URLSearchParams();
        query.set('client_ids', normalizedIds.join(','));
        const res = await api.get(`/api/crm/deals/thermometer-signals?${query.toString()}`, buildCompanyViewConfig());
        return Array.isArray(res.data) ? res.data : [];
    },

    getWhatsAppTicketById: async (ticketId: string, options?: { companyId?: string | null }): Promise<WhatsAppTicketRecord | null> => {
        const res = await api.get(`/api/crm/chat/tickets/${ticketId}`, buildManagedCompanyConfig(buildCompanyViewConfig(), options?.companyId));
        return res.data;
    },

    // ... (rest of methods)

    // Vendas
    getDeals: async (params?: { page?: number, limit?: number, filters?: DealListFilters, view?: string }): Promise<DealListResponse> => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        else queryParams.append('limit', '2000'); // default compatibility limit
        if (params?.view) queryParams.append('view', params.view);

        if (params?.filters?.status && params.filters.status !== 'all') queryParams.append('status', params.filters.status);
        if (params?.filters?.bank && params.filters.bank !== 'all') queryParams.append('bank', params.filters.bank);
        if (params?.filters?.promoter && params.filters.promoter !== 'all') queryParams.append('promoter', params.filters.promoter);
        if (params?.filters?.agentId && params.filters.agentId !== 'all') queryParams.append('agentId', params.filters.agentId);
        if (params?.filters?.startDate) queryParams.append('startDate', params.filters.startDate);
        if (params?.filters?.endDate) queryParams.append('endDate', params.filters.endDate);
        if (params?.filters?.storeId && params.filters.storeId !== 'all') queryParams.append('storeId', params.filters.storeId);

        const res = await api.get(`/api/crm/deals?${queryParams.toString()}`, buildCompanyViewConfig());
        return res.data;
    },

    createDeal: async (data: CreateDealDTO, options?: { companyId?: string | null }) => {
        // Envia snake_case para o backend
        const dealPayload = data as CreateDealDTO & DealPayloadExtras;
        const payload = applyStoreScopeToPayload({
            id: data.id,
            client_id: data.clientId,
            client_name: data.clientName,
            value: data.value,
            product: data.product,
            bank: data.bank,
            commission_rate: data.commissionRate ?? 0,
            commission_total: data.commissionValue ?? 0,
            commission_company: data.companyCommissionValue ?? 0,
            commission_agent: data.userCommissionValue ?? 0,
            company_id: data.companyId ?? undefined,
            agent_id: data.agentId ?? undefined,
            status: data.status || 'pending',
            proposal_number: data.proposalNumber ?? '',
            promoter_id: data.promoterId ?? undefined,
            table_id: data.tableId ?? undefined,
            created_at: data.createdAt ?? undefined,
            import_batch_id: dealPayload.importBatchId ?? undefined,
            segment: data.segment ?? undefined,
            installments: data.installments ?? undefined,
            orgao: data.orgao ?? undefined,
            bank_label: data.bank_label ?? undefined,
            next_follow_up: dealPayload.nextFollowUp ?? undefined,
            interactions: dealPayload.interactions ?? undefined
        }, dealPayload.storeId ?? dealPayload.store_id);
        const response = await api.post<ApiResponse<Deal>>('/api/crm/deals', payload, buildManagedCompanyConfig({}, options?.companyId));
        return response.data;
    },

    updateDeal: async (id: string, updates: Partial<Deal> & { promoterId?: string, tableId?: string }, options?: { companyId?: string | null }): Promise<Deal> => {
        const updatesWithExtras = updates as Partial<Deal> & DealPayloadExtras;
        const basePayload: Record<string, unknown> = {};
        if (updates.status !== undefined) basePayload.status = updates.status;
        if (updates.value !== undefined) basePayload.value = updates.value;
        if (updates.commissionRate !== undefined) basePayload.commission_rate = updates.commissionRate;
        if (updates.commissionValue !== undefined) basePayload.commission_total = updates.commissionValue;
        if (updates.companyCommissionValue !== undefined) basePayload.commission_company = updates.companyCommissionValue;
        if (updates.userCommissionValue !== undefined) basePayload.commission_agent = updates.userCommissionValue;
        if (updates.product !== undefined) basePayload.product = updates.product;
        if (updates.bank !== undefined) basePayload.bank = updates.bank;
        if (updates.proposalNumber !== undefined) basePayload.proposal_number = updates.proposalNumber;
        if (updates.promoterId !== undefined) basePayload.promoter_id = updates.promoterId;
        if (updates.tableId !== undefined) basePayload.table_id = updates.tableId;
        if (updates.createdAt !== undefined) basePayload.created_at = updates.createdAt;
        if (updates.segment !== undefined) basePayload.segment = updates.segment;
        if (updates.installments !== undefined) basePayload.installments = updates.installments;
        if (updates.orgao !== undefined) basePayload.orgao = updates.orgao;
        if (updates.commissionStatus !== undefined) basePayload.commission_status = updates.commissionStatus;
        if (updates.commissionReceivedDate !== undefined) basePayload.commission_received_date = updates.commissionReceivedDate;
        if (updates.agentId !== undefined) basePayload.agent_id = updates.agentId;
        if (updates.cancellationReason !== undefined) basePayload.cancellation_reason = updates.cancellationReason;
        if (updatesWithExtras.nextFollowUp !== undefined) basePayload.next_follow_up = updatesWithExtras.nextFollowUp;
        if (updatesWithExtras.interactions !== undefined) basePayload.interactions = updatesWithExtras.interactions;
        if (updatesWithExtras.importBatchId !== undefined) basePayload.import_batch_id = updatesWithExtras.importBatchId;
        if (updatesWithExtras.storeId !== undefined) basePayload.store_id = updatesWithExtras.storeId;
        if (updatesWithExtras.store_id !== undefined) basePayload.store_id = updatesWithExtras.store_id;
        if (updates.cpf !== undefined) basePayload.client_cpf = updates.cpf;
        if (updates.phone !== undefined) basePayload.client_phone = updates.phone;
        if (updates.email !== undefined) basePayload.client_email = updates.email;
        if (updates.updatedAt !== undefined) basePayload.updated_at = updates.updatedAt;

        const payload = applyStoreScopeToPayload(basePayload, updatesWithExtras.storeId ?? updatesWithExtras.store_id);
        const response = await api.put<ApiResponse<Deal>>(`/api/crm/deals/${id}`, payload, buildManagedCompanyConfig({}, options?.companyId));
        return response.data.data;
    },

    batchUpdateDeals: async (updates: Array<{ id: string, data: Partial<Deal> }>): Promise<{ success: boolean; message?: string }> => {
        const payload = updates.map(u => {
            const data: Record<string, unknown> = {};
            const d = u.data as Partial<Deal> & DealPayloadExtras;
            data.id = u.id;
            if (d.status !== undefined) data.status = d.status;
            if (d.value !== undefined) data.value = d.value;
            if (d.commissionRate !== undefined) data.commission_rate = d.commissionRate;
            if (d.commissionValue !== undefined) data.commission_total = d.commissionValue;
            if (d.companyCommissionValue !== undefined) data.commission_company = d.companyCommissionValue;
            if (d.userCommissionValue !== undefined) data.commission_agent = d.userCommissionValue;
            if (d.promoterId !== undefined) data.promoter_id = d.promoterId;
            if (d.tableId !== undefined) data.table_id = d.tableId;
            if (d.agentPayoutStatus !== undefined) data.agent_payout_status = d.agentPayoutStatus;
            if (d.agentPayoutDate !== undefined) {
                data.agent_payout_date = d.agentPayoutDate;
            }
            if (d.commissionStatus !== undefined) data.commission_status = d.commissionStatus;
            if (d.commissionReceivedDate !== undefined) data.commission_received_date = d.commissionReceivedDate;
            if (d.storeId !== undefined) data.store_id = d.storeId;
            if (d.store_id !== undefined) data.store_id = d.store_id;
            return applyStoreScopeToPayload(data, d.storeId ?? d.store_id);
        });
        const response = await api.post('/api/crm/deals/batch-update', { updates: payload });
        return response.data;
    },

    deleteDeal: async (id: string, options?: { companyId?: string | null }) => { const res = await api.delete(`/api/crm/deals/${id}`, buildManagedCompanyConfig({}, options?.companyId)); return res.data; },
    deleteClient: async (id: string, options?: { companyId?: string | null }) => { const res = await api.delete(`/api/crm/clients/${id}`, buildManagedCompanyConfig({}, options?.companyId)); return res.data; },
    batchDeleteDeals: async (ids: string[]) => { const res = await api.post('/api/crm/deals/batch-delete', { ids }); return res.data; },
    deleteAllDeals: async () => { const res = await api.delete('/api/crm/deals/all', { headers: { 'X-Confirm-Delete': 'YES' } }); return res.data; },
    deleteAllClients: async () => { const res = await api.delete('/api/crm/clients/all', { headers: { 'X-Confirm-Delete': 'YES' } }); return res.data; },
    cleanupDuplicateDeals: async () => {
        const res = await api.post('/api/crm/settings/deals/cleanup-duplicates');
        return res.data;
    },

    getCrmAssistantResponse: async (payload: CrmAssistantRequest): Promise<CrmAssistantResponse> => {
        const res = await api.post('/api/crm/assistant/respond', payload);
        return res.data;
    },

    // --- SUPER ADMIN ---
    getCompanies: async (): Promise<AdminCompanyRecord[]> => {
        const res = await api.get('/api/admin/companies');
        return res.data;
    },
    createCompany: async (data: AdminCompanyPayload): Promise<AdminCompanyRecord> => {
        const res = await api.post('/api/admin/companies', data);
        return res.data;
    },
    updateCompany: async (id: string, data: AdminCompanyPayload): Promise<AdminCompanyRecord> => {
        const res = await api.put(`/api/admin/companies/${id}`, data);
        return res.data;
    },
    getCompanyPayments: async (id: string, limit = 200): Promise<AdminPaymentRecord[]> => {
        const res = await api.get(`/api/admin/companies/${id}/payments?limit=${limit}`);
        return res.data;
    },
    getSuperAdminIntegrationCatalog: async (): Promise<IntegrationCatalogRecord[]> => {
        const res = await api.get('/api/admin/integrations/catalog');
        return res.data;
    },
    updateSuperAdminIntegrationCatalogItem: async (providerKey: string, data: {
        name?: string;
        category?: string;
        segment?: string;
        summary?: string;
        description?: string;
        implementationStatus?: string;
        active: boolean;
        fields?: IntegrationFieldDefinition[];
    }): Promise<IntegrationCatalogRecord> => {
        const res = await api.put(`/api/admin/integrations/catalog/${providerKey}`, data);
        return res.data;
    },
    getAdminSaasSettings: async (): Promise<{ trialEnabled: boolean, trialDays: number }> => {
        const res = await api.get('/api/admin/system/general');
        return res.data;
    },
    saveAdminSaasSettings: async (data: { trialEnabled: boolean, trialDays: number }) => {
        const res = await api.put('/api/admin/system/general', data);
        return res.data;
    },
    getAdminAiSettings: async (): Promise<AdminAiSettings> => {
        const res = await api.get('/api/admin/system/ai');
        return res.data;
    },
    saveAdminAiSettings: async (data: AdminAiSettings): Promise<AdminAiSettings> => {
        const res = await api.put('/api/admin/system/ai', data);
        return res.data;
    },
    testAdminAiConnection: async (data: Partial<AdminAiSettings> & { provider?: 'openai' | 'ollama' }): Promise<{ success: boolean; provider: string; model: string; models: string[] }> => {
        const res = await api.post('/api/admin/system/ai/test-connection', data);
        return res.data;
    },
    getAdminPlans: async (): Promise<AdminSaasPlanRecord[]> => {
        const res = await api.get('/api/admin/saas/plans');
        return res.data;
    },
    createAdminPlan: async (data: AdminSaasPlanPayload) => {
        const res = await api.post('/api/admin/saas/plans', data);
        return res.data;
    },
    updateAdminPlan: async (id: string, data: AdminSaasPlanPayload) => {
        const res = await api.put(`/api/admin/saas/plans/${id}`, data);
        return res.data;
    },
    deleteAdminPlan: async (id: string) => {
        const res = await api.delete(`/api/admin/saas/plans/${id}`);
        return res.data;
    },
    deleteCompany: async (id: string): Promise<void> => {
        await api.delete(`/api/admin/companies/${id}`);
    },
    updateCompanyStatus: async (id: string, status: 'active' | 'blocked' | 'pending_payment') => {
        const res = await api.put(`/api/admin/companies/${id}/status`, { status });
        return res.data;
    },
    updateCompanyBillingProfile: async (id: string, data: { asaasCustomerId?: string | null, cpfCnpj?: string | null, phone?: string | null }) => {
        const res = await api.put(`/api/admin/companies/${id}/billing`, data);
        return res.data;
    },
    getCompanyWhatsappMetaProviderSettings: async (id: string): Promise<CompanyWhatsappMetaProviderSettings> => {
        const res = await api.get(`/api/admin/companies/${id}/whatsapp-meta-provider`);
        return res.data;
    },
    updateCompanyWhatsappMetaProviderSettings: async (id: string, data: Partial<CompanyWhatsappMetaProviderSettings>): Promise<CompanyWhatsappMetaProviderSettings> => {
        const res = await api.put(`/api/admin/companies/${id}/whatsapp-meta-provider`, data);
        return res.data;
    },
    getAdminWhatsappMarketingBlacklist: async (limit = 200): Promise<AdminWhatsAppMarketingBlacklistSummary> => {
        const res = await api.get(`/api/admin/system/whatsapp-marketing-blacklist?limit=${limit}`);
        return res.data;
    },
    getAdminSecurityEvents: async (params?: {
        companyId?: string;
        severity?: AdminSecurityEventSeverity | 'all';
        eventType?: string;
        source?: string;
        limit?: number;
    }): Promise<AdminSecurityEventListResponse> => {
        const normalizedParams = {
            ...params,
            severity: params?.severity === 'all' ? undefined : params?.severity
        };
        const res = await api.get('/api/admin/security-events', { params: normalizedParams });
        return res.data;
    },
    importAdminWhatsappMarketingBlacklist: async (file: File, payload?: { mode?: 'merge' | 'replace'; reason?: string }): Promise<AdminWhatsAppMarketingBlacklistSummary> => {
        const formData = new FormData();
        formData.append('file', file);
        if (payload?.mode) formData.append('mode', payload.mode);
        if (payload?.reason) formData.append('reason', payload.reason);
        const res = await api.post('/api/admin/system/whatsapp-marketing-blacklist/import', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return res.data;
    },
    deleteAdminWhatsappMarketingBlacklistEntry: async (id: number) => {
        const res = await api.delete(`/api/admin/system/whatsapp-marketing-blacklist/${id}`);
        return res.data;
    },
    createCompanyPayment: async (id: string, data: { asaasPaymentId: string, value: number, status?: string, billingType?: string, dueDate?: string | null, paymentDate?: string | null }) => {
        const res = await api.post(`/api/admin/companies/${id}/payments`, data);
        return res.data;
    },

    getCompanyNetworkContext: async (authToken?: string): Promise<CompanyNetworkContext> => {
        const res = await api.get('/api/crm/network/context', authToken
            ? { headers: { Authorization: `Bearer ${authToken}` } }
            : undefined);
        return res.data;
    },

    createSubcompany: async (data: {
        name: string;
        email?: string;
        document?: string;
        ownerName: string;
        ownerEmail: string;
        ownerPassword: string;
        ownerPhone?: string;
        commissionSplit?: number | string;
    }) => {
        const res = await api.post('/api/crm/network/subcompanies', data);
        return res.data;
    },

    getCompanyStoresContext: async (): Promise<CompanyStoresContext> => {
        const res = await api.get('/api/crm/network/stores');
        return res.data;
    },

    getCompanyStore: async (id: string): Promise<CompanyStoreDetail> => {
        const res = await api.get(`/api/crm/network/stores/${id}`);
        return res.data;
    },

    createCompanyStore: async (data: {
        name: string;
        tradeName?: string;
        code?: string;
        status?: 'active' | 'inactive' | 'onboarding';
        managerUserId?: string | null;
        city?: string;
        state?: string;
        address?: string;
        phone?: string;
        email?: string;
        activatedAt?: string | null;
        monthlyGoal?: number;
        notes?: string;
        color?: string;
        logoUrl?: string;
        assignedUserIds?: string[];
    }): Promise<CompanyStoreDetail> => {
        const res = await api.post('/api/crm/network/stores', data);
        return res.data;
    },

    updateCompanyStore: async (id: string, data: {
        name: string;
        tradeName?: string;
        code?: string;
        status?: 'active' | 'inactive' | 'onboarding';
        managerUserId?: string | null;
        city?: string;
        state?: string;
        address?: string;
        phone?: string;
        email?: string;
        activatedAt?: string | null;
        monthlyGoal?: number;
        notes?: string;
        color?: string;
        logoUrl?: string;
        assignedUserIds?: string[];
    }): Promise<CompanyStoreDetail> => {
        const res = await api.put(`/api/crm/network/stores/${id}`, data);
        return res.data;
    },

    impersonateSubcompany: async (companyId: string, authToken?: string) => {
        const res = await api.post(`/api/crm/network/subcompanies/${companyId}/impersonate`, {}, authToken
            ? { headers: { Authorization: `Bearer ${authToken}` } }
            : undefined);
        return res.data;
    },

    // --- ASAAS INTEGRATION ---
    createAsaasCustomer: async (data: { name: string, email: string, cpfCnpj: string, mobilePhone?: string, externalReference: string }) => {
        const res = await api.post('/api/crm/asaas/customers', data);
        return res.data.id;
    },

    createAsaasSubscription: async (data: { customer: string, billingType: string, value: number, nextDueDate: string, cycle: string, description: string }) => {
        const res = await api.post('/api/crm/asaas/subscriptions', data);
        return res.data;
    },

    createAsaasPayment: async (data: { customer: string, billingType: string, value: number, dueDate: string, description: string }) => {
        const res = await api.post('/api/crm/asaas/payments', data);
        return {
            asaasId: res.data.id,
            invoiceUrl: res.data.bankSlipUrl || res.data.invoiceUrl || res.data.paymentLink
        };
    },

    getAsaasPayments: async (asaasCustomerId: string, limit = 50) => {
        const res = await api.get(`/api/crm/asaas/customers/${asaasCustomerId}/payments?limit=${limit}`);
        return res.data;
    },

    changeOwnPlan: async (planId: string) => {
        const res = await api.put('/api/crm/billing/plan', { planId });
        return res.data;
    },

    // --- Auxiliares (SaaS Settings) ---
    getAuxiliarySettings: async () => {
        const res = await api.get('/api/crm/settings/auxiliary', buildCompanyViewConfig());
        return res.data;
    },
    getCrmWorkflowSettings: async (): Promise<CrmWorkflowSettingsResponse> => {
        const res = await api.get('/api/crm/settings/workflow-config', buildCompanyViewConfig());
        return res.data;
    },
    saveCrmWorkflowSettings: async (payload: { profiles: CrmWorkflowProfile[] }): Promise<CrmWorkflowSettingsResponse> => {
        const res = await api.put('/api/crm/settings/workflow-config', payload, buildCompanyViewConfig());
        return res.data;
    },
    getCompanyIntegrations: async (): Promise<{
        catalog: IntegrationCatalogRecord[];
        integrations: CompanyIntegrationRecord[];
    }> => {
        const res = await api.get('/api/crm/settings/integrations');
        return res.data;
    },
    getCompanyWhatsAppAiSettings: async (): Promise<CompanyWhatsAppAiSettingsResponse> => {
        const res = await api.get('/api/wa/admin/ai-settings');
        return res.data;
    },
    saveCompanyWhatsAppAiSettings: async (payload: CompanyWhatsAppAiSettings): Promise<CompanyWhatsAppAiSettingsResponse> => {
        const res = await api.put('/api/wa/admin/ai-settings', payload);
        return res.data;
    },
    getWhatsAppAiReviews: async (params?: {
        reviewStatus?: WhatsAppAiReviewStatus | 'all';
        instanceId?: number | null;
        search?: string;
        limit?: number;
        offset?: number;
    }): Promise<WhatsAppAiReviewListResponse> => {
        const res = await api.get('/api/wa/admin/ai-reviews', { params });
        return res.data;
    },
    getWhatsAppAiReview: async (runId: string): Promise<WhatsAppAiReviewDetail> => {
        const res = await api.get(`/api/wa/admin/ai-reviews/${runId}`);
        return res.data;
    },
    saveWhatsAppAiReview: async (runId: string, payload: {
        reviewStatus: WhatsAppAiReviewStatus;
        reviewComment?: string;
        reviewSuggestedReply?: string;
    }): Promise<WhatsAppAiReviewDetail> => {
        const res = await api.put(`/api/wa/admin/ai-reviews/${runId}`, payload);
        return res.data;
    },
    getAdminWhatsAppAiReviews: async (params?: {
        companyId?: string;
        reviewStatus?: WhatsAppAiReviewStatus | 'all';
        instanceId?: number | null;
        search?: string;
        limit?: number;
        offset?: number;
    }): Promise<WhatsAppAiReviewListResponse> => {
        const res = await api.get('/api/admin/system/ai/reviews', { params });
        return res.data;
    },
    getAdminWhatsAppAiReview: async (runId: string): Promise<WhatsAppAiReviewDetail> => {
        const res = await api.get(`/api/admin/system/ai/reviews/${runId}`);
        return res.data;
    },
    saveAdminWhatsAppAiReview: async (runId: string, payload: {
        reviewStatus: WhatsAppAiReviewStatus;
        reviewComment?: string;
        reviewSuggestedReply?: string;
    }): Promise<WhatsAppAiReviewDetail> => {
        const res = await api.put(`/api/admin/system/ai/reviews/${runId}`, payload);
        return res.data;
    },
    getAdminLegacyAiExamples: async (params?: {
        trainingStatus?: LegacyAiExampleStatus | 'all';
        productHint?: string;
        intentHint?: string;
        sourceSystem?: string;
        search?: string;
        limit?: number;
        offset?: number;
    }): Promise<LegacyAiExampleListResponse> => {
        const res = await api.get('/api/admin/system/ai/legacy-examples', { params });
        return res.data;
    },
    getAdminLegacyAiExample: async (exampleId: string): Promise<LegacyAiExampleDetail> => {
        const res = await api.get(`/api/admin/system/ai/legacy-examples/${exampleId}`);
        return res.data;
    },
    saveAdminLegacyAiExample: async (exampleId: string, payload: {
        trainingStatus: LegacyAiExampleStatus;
        reviewComment?: string;
        reviewSuggestedReply?: string;
    }): Promise<LegacyAiExampleDetail> => {
        const res = await api.put(`/api/admin/system/ai/legacy-examples/${exampleId}`, payload);
        return res.data;
    },
    saveCompanyIntegration: async (
        providerKey: string,
        payload: {
            enabled: boolean;
            credentials: Record<string, string>;
        }
    ): Promise<CompanyIntegrationRecord> => {
        const res = await api.put(`/api/crm/settings/integrations/${providerKey}`, payload);
        return res.data;
    },
    testCompanySmsConnection: async (
        providerKey = 'comtele_sms'
    ): Promise<CompanySmsConnectionResponse> => {
        const res = await api.post('/api/crm/sms/test-connection', { providerKey });
        return res.data;
    },
    getCompanySmsBalance: async (
        providerKey = 'comtele_sms'
    ): Promise<CompanySmsConnectionResponse> => {
        const res = await api.get('/api/crm/sms/balance', { params: { providerKey } });
        return res.data;
    },
    sendCompanySms: async (payload: {
        providerKey?: string;
        clientId?: string;
        dealId?: string;
        phone?: string;
        content: string;
        scheduledFor?: string;
        sender?: string;
    }): Promise<CompanySmsSendResponse> => {
        const res = await api.post('/api/crm/sms/send', payload);
        return res.data;
    },
    getLatestEnrichmentConsult: async (
        providerKey: string,
        clientId: string,
        options?: { companyId?: string | null }
    ): Promise<{ record: EnrichmentConsultRecord | null }> => {
        const res = await api.get(
            `/api/crm/enrichment/${providerKey}/clients/${clientId}/latest`,
            buildManagedCompanyConfig({}, options?.companyId)
        );
        return res.data;
    },
    consultEnrichmentDocument: async (
        providerKey: string,
        payload: {
            clientId?: string;
            document?: string;
            forceRefresh?: boolean;
        },
        options?: { companyId?: string | null }
    ): Promise<{
        success: boolean;
        providerKey: string;
        providerName: string;
        tokenPresent?: boolean;
        fromCache?: boolean;
        cacheScope?: 'company' | 'global' | 'provider';
        cachedAt?: string | null;
        record: EnrichmentConsultRecord;
    }> => {
        const res = await api.post(
            `/api/crm/enrichment/${providerKey}/consult`,
            payload,
            buildManagedCompanyConfig({}, options?.companyId)
        );
        return res.data;
    },
    getProviderIntegrationStatus: async (providerKey: string): Promise<ProviderIntegrationStatus> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/status`);
        return res.data;
    },
    listAvailableBankingProviders: async (params: {
        segment?: string;
        capability?: string;
    } = {}): Promise<{ providers: IntegrationCatalogRecord[] }> => {
        const res = await api.get('/api/crm/provider-integrations/available-providers', { params });
        return res.data;
    },
    testProviderIntegrationConnection: async (providerKey: string): Promise<{ success: boolean; providerKey: string; tokenPresent?: boolean }> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/test-connection`);
        return res.data;
    },
    simulateFgtsWithProvider: async (providerKey: string, payload: {
        cpf: string;
        installments: number;
        birthDate?: string;
        simulationType?: string;
        requestedAmount?: number;
        covenantCode?: string;
        authorizationTarget?: string;
        fgtsAuthorizationTarget?: string;
        provider?: string;
        bancarizador?: string;
        bankarizer?: string;
    }): Promise<{
        providerKey: string;
        cpf: string;
        installments: number;
        raw: unknown;
        simulations: NovoSaqueFgtsSimulationResult[];
    }> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/fgts/simulations`, payload);
        return res.data;
    },
    createFgtsProposalWithProvider: async (providerKey: string, payload: {
        cpf: string;
        simulationPayload: unknown[];
        selectedSimulation?: unknown;
        customer: ApiJsonRecord;
    }): Promise<{
        providerKey: string;
        raw: unknown;
        serviceId?: string | null;
    }> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/fgts/proposals`, payload);
        return res.data;
    },
    listFgtsProposalsWithProvider: async (
        providerKey: string,
        params: {
            search?: string;
            page?: number;
            limit?: number;
            dataPropostaInicio?: string;
            dataPropostaFim?: string;
            startDate?: string;
            endDate?: string;
        } = {}
    ): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/fgts/proposals`, { params });
        return res.data;
    },
    getFgtsProposalWithProvider: async (providerKey: string, proposalId: string): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/fgts/proposals/${proposalId}`);
        return res.data;
    },
    getFgtsProposalFormalizationLinkWithProvider: async (providerKey: string, proposalId: string): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/fgts/proposals/${proposalId}/formalization-link`);
        return res.data;
    },
    updateFgtsProposalWithProvider: async (providerKey: string, proposalId: string, payload: ApiJsonRecord): Promise<ApiJsonRecord> => {
        const res = await api.put(`/api/crm/provider-integrations/${providerKey}/fgts/proposals/${proposalId}`, payload);
        return res.data;
    },
    cancelFgtsProposalWithProvider: async (providerKey: string, proposalId: string): Promise<ApiJsonRecord> => {
        const res = await api.delete(`/api/crm/provider-integrations/${providerKey}/fgts/proposals/${proposalId}`);
        return res.data;
    },
    createFgtsCustomerWithProvider: async (providerKey: string, payload: {
        cpf: string;
        customerServiceId: string;
        customer: ApiJsonRecord;
    }): Promise<{
        providerKey: string;
        raw: unknown;
        customerId?: string | null;
        onboardingLink?: string;
    }> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/fgts/customers`, payload);
        return res.data;
    },
    createFgtsContractWithProvider: async (providerKey: string, payload: {
        selectedSimulation: unknown;
        simulationId: string;
        customerId: string;
        contract?: ApiJsonRecord;
    }): Promise<{
        providerKey: string;
        raw: unknown;
        contractId?: string | null;
        onboardingLink?: string;
    }> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/fgts/contracts`, payload);
        return res.data;
    },
    getFgtsContractWithProvider: async (providerKey: string, contractId: string): Promise<{
        providerKey: string;
        raw: unknown;
        contractId: string;
        onboardingLink?: string;
    }> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/fgts/contracts/${contractId}`);
        return res.data;
    },
    getFgtsAsyncBalanceWithProvider: async (providerKey: string, cpf: string): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/fgts/async/${cpf}`);
        return res.data;
    },
    getFgtsTransactionStatusWithProvider: async (providerKey: string, transactionId: string): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/fgts/transactions/${transactionId}`);
        return res.data;
    },
    listBankingOperations: async (limit = 50): Promise<{ operations: BankingOperationRecord[] }> => {
        const res = await api.get('/api/crm/provider-integrations/operations', {
            params: { limit }
        });
        return res.data;
    },
    refreshBankingOperation: async (operationId: string): Promise<BankingOperationRecord> => {
        const res = await api.post(`/api/crm/provider-integrations/operations/${operationId}/refresh`);
        return res.data;
    },
    listBankingConsents: async (limit = 100): Promise<{ consents: BankingConsentRecord[] }> => {
        const res = await api.get('/api/crm/provider-integrations/consents', {
            params: { limit }
        });
        return res.data;
    },
    createBankingOperation: async (payload: {
        providerKey: string;
        providerName: string;
        clientId?: string;
        clientName: string;
        clientCpf?: string;
        dealId?: string;
        dealProposalNumber?: string;
        crmContext?: {
            productName?: string;
            bankId?: string;
            bankName?: string;
            promoterId?: string;
            promoterName?: string;
            tableId?: string;
            tableName?: string;
        };
        result?: ApiJsonRecord;
        customerSnapshot?: ApiJsonRecord;
        status?: string;
        statusLabel?: string;
        lastError?: string;
    }): Promise<BankingOperationRecord> => {
        const res = await api.post('/api/crm/provider-integrations/operations', payload);
        return res.data;
    },
    upsertBankingConsent: async (payload: {
        providerKey: string;
        providerName: string;
        consultId: string;
        clientId?: string;
        clientName: string;
        clientCpf?: string;
        crmContext?: {
            productName?: string;
            bankId?: string;
            bankName?: string;
            promoterId?: string;
            promoterName?: string;
            tableId?: string;
            tableName?: string;
        };
        authorizationLink?: string;
        status?: string;
        statusLabel?: string;
        statusCode?: number | null;
        requiresLinkSelection?: boolean;
        selectedLinkId?: string;
        linkedOperationId?: string;
        linkedDealId?: string;
        consultSnapshot?: ApiJsonRecord;
        simulationSnapshot?: ApiJsonRecord;
        customerSnapshot?: ApiJsonRecord;
        flowState?: ApiJsonRecord;
        providerResponse?: ApiJsonRecord;
        lastError?: string;
    }): Promise<BankingConsentRecord> => {
        const res = await api.post('/api/crm/provider-integrations/consents', payload);
        return res.data;
    },
    getLatestBankingSimulation: async (params: {
        segment: string;
        providerKey: string;
        clientId?: string;
        cpf?: string;
    }): Promise<BankingSimulationCacheRecord | null> => {
        const res = await api.get('/api/crm/provider-integrations/simulations/latest', {
            params
        });
        return res.data?.simulation || null;
    },
    upsertBankingSimulation: async (payload: {
        segment: string;
        providerKey: string;
        providerName: string;
        clientId?: string;
        clientName: string;
        clientCpf?: string;
        crmContext?: {
            productName?: string;
            bankId?: string;
            bankName?: string;
            promoterId?: string;
            promoterName?: string;
            tableId?: string;
            tableName?: string;
        };
        requestSnapshot?: ApiJsonRecord;
        responseSnapshot?: ApiJsonRecord;
        customerSnapshot?: ApiJsonRecord;
        uiSnapshot?: ApiJsonRecord;
        lastError?: string;
    }): Promise<BankingSimulationCacheRecord> => {
        const res = await api.post('/api/crm/provider-integrations/simulations/cache', payload);
        return res.data;
    },
    listBankingHygieneHistory: async (params: {
        providerKey?: string;
        segment?: string;
        source?: string;
        clientId?: string;
        cpf?: string;
        limit?: number;
    } = {}): Promise<{ history: BankingHygieneHistoryRecord[] }> => {
        const res = await api.get('/api/crm/provider-integrations/hygiene-history', { params });
        return res.data;
    },
    createBankingHygieneHistory: async (payload: {
        segment: string;
        providerKey: string;
        providerName: string;
        source: string;
        sourceId?: string;
        clientId?: string;
        clientName: string;
        clientCpf: string;
        clientPhone?: string;
        clientEmail?: string;
        status: string;
        statusLabel?: string;
        offerValue?: number | null;
        installments?: number | null;
        tableName?: string;
        simulationId?: string | null;
        offerCount?: number | null;
        message?: string;
        requestSnapshot?: ApiJsonRecord;
        responseSnapshot?: ApiJsonRecord;
        customerSnapshot?: ApiJsonRecord;
        uiSnapshot?: ApiJsonRecord;
        lastError?: string;
    }): Promise<BankingHygieneHistoryRecord> => {
        const res = await api.post('/api/crm/provider-integrations/hygiene-history', payload);
        return res.data;
    },
    runFgtsContractFlowWithProvider: async (providerKey: string, payload: {
        cpf: string;
        simulationPayload?: unknown[];
        simulationResponse?: unknown[];
        selection?: ApiJsonRecord;
        customer: ApiJsonRecord;
        contract?: ApiJsonRecord;
    }): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/fgts/contract-flow`, payload);
        return res.data;
    },
    createCltConsultWithProvider: async (providerKey: string, payload: ApiJsonRecord): Promise<{
        providerKey: string;
        raw: unknown;
        consultId?: string | null;
        status?: string | null;
        statusCode?: number | null;
        statusLabel?: string | null;
        statusDescription?: string | null;
        description?: string | null;
        authorizationLink?: string | null;
        expiresAt?: string | null;
        rawStatus?: string | null;
        availableMarginValue?: string | null;
        documentNumber?: string | null;
        name?: string | null;
    }> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/clt/consults`, payload);
        return res.data;
    },
    listCltConsultsWithProvider: async (
        providerKey: string,
        params: ApiJsonRecord
    ): Promise<{
        providerKey: string;
        pages?: ApiJsonRecord;
        data?: CltConsultRecord[];
    }> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/clt/consults`, { params });
        return res.data;
    },
    getCltConsultAuthorizationStatusWithProvider: async (
        providerKey: string,
        consultId: string,
        params: {
            cpf?: string;
            selectedLinkId?: string;
        } = {}
    ): Promise<{
        providerKey: string;
        consultId: string | null;
        status?: string | null;
        statusCode?: number | null;
        statusLabel?: string | null;
        statusDescription?: string | null;
        authorizationLink?: string | null;
        expiresAt?: string | null;
        rawStatus?: string | null;
        requiresLinkSelection?: boolean;
        selectedLinkId?: string;
        availableMarginValue?: string | null;
        documentNumber?: string | null;
        name?: string | null;
        description?: string | null;
        vinculos?: ApiJsonList;
        flowState?: ApiJsonRecord;
        consult?: ApiJsonRecord;
        consent?: BankingConsentRecord | null;
    }> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/clt/consults/${consultId}/authorization-status`, { params });
        return res.data;
    },
    authorizeCltConsultWithProvider: async (providerKey: string, consultId: string, payload: ApiJsonRecord = {}): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/clt/consults/${consultId}/authorize`, payload);
        return res.data;
    },
    createCltOfferWithProvider: async (providerKey: string, payload: ApiJsonRecord): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/clt/offers`, payload);
        return res.data;
    },
    listCltConfigsWithProvider: async (providerKey: string, params: ApiJsonRecord = {}): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/clt/configs`, { params });
        return res.data;
    },
    createCltSimulationWithProvider: async (providerKey: string, payload: ApiJsonRecord): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/clt/simulations`, payload);
        return res.data;
    },
    createCltOperationWithProvider: async (providerKey: string, payload: ApiJsonRecord): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/clt/operations`, payload);
        return res.data;
    },
    updateCltOperationWithProvider: async (providerKey: string, operationId: string, payload: ApiJsonRecord): Promise<ApiJsonRecord> => {
        const res = await api.put(`/api/crm/provider-integrations/${providerKey}/clt/operations/${operationId}`, payload);
        return res.data;
    },
    listCltOperationsWithProvider: async (
        providerKey: string,
        params: {
            startDate: string;
            endDate: string;
            limit?: number;
            page?: number;
            search?: string;
            status?: string;
            provider?: string;
        }
    ): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/clt/operations`, { params });
        return res.data;
    },
    getCltOperationWithProvider: async (providerKey: string, operationId: string): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/clt/operations/${operationId}`);
        return res.data;
    },
    getCltOperationFormalizationLinkWithProvider: async (providerKey: string, operationId: string): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/clt/operations/${operationId}/formalization-link`);
        return res.data;
    },
    cancelCltOperationWithProvider: async (providerKey: string, operationId: string, payload: ApiJsonRecord): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/clt/operations/${operationId}/cancel`, payload);
        return res.data;
    },
    updateCltPaymentPendencyWithProvider: async (providerKey: string, operationId: string, payload: ApiJsonRecord): Promise<ApiJsonRecord> => {
        const res = await api.patch(`/api/crm/provider-integrations/${providerKey}/clt/operations/${operationId}/pendency/payment-data`, payload);
        return res.data;
    },
    listConsignedPublicAgenciesWithProvider: async (providerKey: string, params: ApiJsonRecord = {}): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/consigned/public-agencies`, { params });
        return res.data;
    },
    createConsignedSimulationWithProvider: async (providerKey: string, payload: ApiJsonRecord): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/consigned/simulations`, payload);
        return res.data;
    },
    createConsignedProposalWithProvider: async (providerKey: string, payload: ApiJsonRecord): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/consigned/proposals`, payload);
        return res.data;
    },
    listConsignedProposalsWithProvider: async (providerKey: string, params: ApiJsonRecord = {}): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/consigned/proposals`, { params });
        return res.data;
    },
    getConsignedProposalWithProvider: async (providerKey: string, proposalId: string): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/consigned/proposals/${proposalId}`);
        return res.data;
    },
    getConsignedProposalFormalizationLinkWithProvider: async (providerKey: string, proposalId: string): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/provider-integrations/${providerKey}/consigned/proposals/${proposalId}/formalization-link`);
        return res.data;
    },
    moveConsignedProposalWithProvider: async (providerKey: string, proposalId: string, payload: ApiJsonRecord): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/consigned/proposals/${proposalId}/movement`, payload);
        return res.data;
    },
    cancelConsignedProposalWithProvider: async (providerKey: string, proposalId: string, payload: ApiJsonRecord = {}): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/provider-integrations/${providerKey}/consigned/proposals/${proposalId}/cancel`, payload);
        return res.data;
    },
    createCommissionTables: async (tables: CommissionTable[]): Promise<CommissionTable[]> => {
        const res = await api.post('/api/crm/settings/tables/bulk', { tables });
        return res.data;
    },
    createCommissionTable: async (data: CommissionTable): Promise<CommissionTable> => {
        const res = await api.post<CommissionTable>('/api/crm/settings/tables', data);
        return res.data;
    },
    updateCommissionTable: async (id: string, data: Partial<CommissionTable>): Promise<CommissionTable> => {
        const res = await api.put<CommissionTable>(`/api/crm/settings/tables/${id}`, data);
        return res.data;
    },
    deleteCommissionTable: async (id: string): Promise<void> => {
        await api.delete(`/api/crm/settings/tables/${id}`);
    },
    uploadBranding: async (logoFile?: File | null, faviconFile?: File | null): Promise<{ logoUrl?: string, faviconUrl?: string }> => {
        const formData = new FormData();
        if (logoFile) formData.append('logo', logoFile);
        if (faviconFile) formData.append('favicon', faviconFile);

        const res = await api.post<{ logoUrl?: string, faviconUrl?: string }>('/api/crm/settings/upload-branding', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return res.data;
    },
    getBrandingSettings: async (): Promise<{
        primaryColor: string;
        sidebarColor: string;
        appName: string;
        logoUrl: string;
        faviconUrl: string;
        dashboard: {
            showCompanyProfit: boolean;
            showTotalRepasse: boolean;
        };
    }> => {
        const res = await api.get('/api/crm/settings/branding');
        return res.data;
    },
    saveBrandingSettings: async (settings: {
        primaryColor: string;
        sidebarColor: string;
        appName: string;
        logoUrl: string;
        faviconUrl: string;
        dashboard: {
            showCompanyProfit: boolean;
            showTotalRepasse: boolean;
        };
    }) => {
        const res = await api.put('/api/crm/settings/branding', settings);
        return res.data;
    },
    getPublicSelfServiceSettings: async (): Promise<PublicSelfServiceSettings> => {
        const res = await api.get<PublicSelfServiceSettings>('/api/crm/settings/public-self-service');
        return res.data;
    },
    savePublicSelfServiceSettings: async (settings: Omit<PublicSelfServiceSettings, 'publicPath'>): Promise<PublicSelfServiceSettings> => {
        const res = await api.put<PublicSelfServiceSettings>('/api/crm/settings/public-self-service', settings);
        return res.data;
    },
    getSecureBrowserSettings: async (): Promise<SecureBrowserSettings> => {
        const res = await api.get('/api/crm/secure-browser/settings');
        return res.data;
    },
    saveSecureBrowserSettings: async (settings: SecureBrowserSettings): Promise<SecureBrowserSettings> => {
        const res = await api.put('/api/crm/secure-browser/settings', settings);
        return res.data;
    },
    openSecureBrowser: async (targetUrl: string): Promise<{ ok: boolean; targetUrl: string }> => {
        const res = await api.post('/api/crm/secure-browser/open', { targetUrl });
        return res.data;
    },
    closeSecureBrowser: async (): Promise<{ ok: boolean }> => {
        const res = await api.post('/api/crm/secure-browser/close');
        return res.data;
    },
    getDashboardPreferences: async (): Promise<{
        effectivePreferences: {
            selectedPerspective: 'executive' | 'commercial' | 'bank' | 'promoter' | 'segment' | 'product';
            widgetsByPerspective: Record<string, string[]>;
        };
        userPreferences: {
            selectedPerspective: 'executive' | 'commercial' | 'bank' | 'promoter' | 'segment' | 'product';
            widgetsByPerspective: Record<string, string[]>;
        } | null;
        profiles: Record<string, {
            selectedPerspective: 'executive' | 'commercial' | 'bank' | 'promoter' | 'segment' | 'product';
            widgetsByPerspective: Record<string, string[]>;
        }>;
        activeRoleKey: string;
        canManageProfiles: boolean;
    }> => {
        const res = await api.get('/api/crm/settings/dashboard/preferences');
        return res.data;
    },
    saveDashboardPreferences: async (preferences: {
        selectedPerspective: 'executive' | 'commercial' | 'bank' | 'promoter' | 'segment' | 'product';
        widgetsByPerspective: Record<string, string[]>;
    }) => {
        const res = await api.put('/api/crm/settings/dashboard/preferences', preferences);
        return res.data;
    },
    resetDashboardPreferences: async () => {
        const res = await api.delete('/api/crm/settings/dashboard/preferences');
        return res.data;
    },
    saveDashboardProfile: async (
        roleKey: 'administrador' | 'consultor' | 'supervisor',
        preferences: {
            selectedPerspective: 'executive' | 'commercial' | 'bank' | 'promoter' | 'segment' | 'product';
            widgetsByPerspective: Record<string, string[]>;
        }
    ) => {
        const res = await api.put(`/api/crm/settings/dashboard/profiles/${roleKey}`, preferences);
        return res.data;
    },

    getNotifications: async (limit = 50): Promise<Array<{
        id: string;
        title: string;
        message: string;
        type: 'success' | 'warning' | 'error' | 'info';
        read: boolean;
        createdAt: string;
        targetUserId?: string;
    }>> => {
        const res = await api.get('/api/crm/notifications', { params: { limit } });
        return res.data;
    },
    createNotification: async (data: {
        title: string;
        message: string;
        type?: 'success' | 'warning' | 'error' | 'info';
        targetUserId?: string;
    }) => {
        const res = await api.post('/api/crm/notifications', data);
        return res.data;
    },
    markNotificationRead: async (id: string) => {
        const res = await api.put(`/api/crm/notifications/${id}/read`);
        return res.data;
    },
    markAllNotificationsRead: async () => {
        const res = await api.post('/api/crm/notifications/read-all');
        return res.data;
    },
    getPushNotificationPublicKey: async (): Promise<{ enabled: boolean; publicKey: string | null }> => {
        const res = await api.get('/api/crm/notifications/push/public-key');
        return res.data;
    },
    savePushNotificationSubscription: async (subscription: PushSubscriptionJSON) => {
        const res = await api.post('/api/crm/notifications/push/subscriptions', { subscription });
        return res.data;
    },
    deletePushNotificationSubscription: async (endpoint?: string | null) => {
        const res = await api.delete('/api/crm/notifications/push/subscriptions', {
            data: { endpoint: endpoint || '' }
        });
        return res.data;
    },

    getUsefulLinks: async (): Promise<UsefulLink[]> => {
        const res = await api.get('/api/crm/settings/useful-links');
        return res.data;
    },
    createUsefulLink: async (data: Omit<UsefulLink, 'id' | 'createdAt' | 'updatedAt'>): Promise<UsefulLink> => {
        const res = await api.post('/api/crm/settings/useful-links', data);
        return res.data;
    },
    updateUsefulLink: async (id: string, data: Omit<UsefulLink, 'id' | 'createdAt' | 'updatedAt'>): Promise<UsefulLink> => {
        const res = await api.put(`/api/crm/settings/useful-links/${id}`, data);
        return res.data;
    },
    deleteUsefulLink: async (id: string) => {
        const res = await api.delete(`/api/crm/settings/useful-links/${id}`);
        return res.data;
    },

    getWhatsAppTemplates: async (): Promise<WhatsAppTemplate[]> => {
        const res = await api.get('/api/crm/settings/whatsapp-templates');
        return res.data;
    },
    createWhatsAppTemplate: async (data: Omit<WhatsAppTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<WhatsAppTemplate> => {
        const res = await api.post('/api/crm/settings/whatsapp-templates', data);
        return res.data;
    },
    updateWhatsAppTemplate: async (id: string, data: Omit<WhatsAppTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<WhatsAppTemplate> => {
        const res = await api.put(`/api/crm/settings/whatsapp-templates/${id}`, data);
        return res.data;
    },
    deleteWhatsAppTemplate: async (id: string) => {
        const res = await api.delete(`/api/crm/settings/whatsapp-templates/${id}`);
        return res.data;
    },

    // Mailing
    importMailingLeads: async (formData: FormData): Promise<{ count: number; readCount?: number; ignoredCount?: number; campaignId?: string }> => {
        const res = await api.post<{ count: number; readCount?: number; ignoredCount?: number; campaignId?: string }>('/api/crm/mailing/leads/bulk', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return res.data;
    },
    getMailingCampaigns: async (): Promise<Array<{ id: string; name: string; status: string; created_at: string; lead_count?: number }>> => {
        const res = await api.get('/api/crm/mailing/campaigns', buildCompanyViewConfig());
        return res.data;
    },
    getMailingCampaignSample: async (campaignId: string, limit = 5, preset?: string): Promise<{ items: ApiJsonList; total: number; }> => {
        const res = await api.get(`/api/crm/mailing/campaigns/${campaignId}/sample`, buildCompanyViewConfig({
            params: { limit, preset }
        }));
        if (Array.isArray(res.data)) {
            return { items: res.data, total: res.data.length };
        }
        return {
            items: Array.isArray(res.data?.items) ? res.data.items : [],
            total: Number(res.data?.total || 0)
        };
    },
    getMailingLeads: async (params: { search?: string, campaignId?: string, segment?: string, page?: number, limit?: number }): Promise<{ leads: MailingLead[], total: number }> => {
        const query = new URLSearchParams();
        if (params.search) query.append('search', params.search);
        if (params.campaignId) query.append('campaignId', params.campaignId);
        if (params.segment) query.append('segment', params.segment);
        if (params.page) query.append('page', String(params.page));
        if (params.limit) query.append('limit', String(params.limit));
        const res = await api.get(`/api/crm/mailing/leads?${query.toString()}`, buildCompanyViewConfig());
        return res.data;
    },
    deleteMailingCampaign: async (name: string): Promise<void> => { const encoded = encodeURIComponent(name); await api.delete(`/api/crm/mailing/campaigns/by-name/${encoded}`); },
    deleteAllMailingData: async () => {
        const res = await api.delete('/api/crm/settings/mailing/all');
        return res.data;
    },
    updateMailingLead: async (id: string, data: Partial<MailingLead>, options?: { companyId?: string | null }): Promise<MailingLead> => {
        const res = await api.put<MailingLead>(`/api/crm/mailing/leads/${id}`, data, buildManagedCompanyConfig({}, options?.companyId));
        return res.data;
    },
    deleteMailingLead: async (id: string, options?: { companyId?: string | null }): Promise<void> => {
        await api.delete(`/api/crm/mailing/leads/${id}`, buildManagedCompanyConfig({}, options?.companyId));
    },

    // --- Interactions ---
    getInteractions: async (leadId: string, options?: { companyId?: string | null }): Promise<Interaction[]> => {
        const res = await api.get(`/api/crm/mailing/leads/${leadId}/interactions`, buildManagedCompanyConfig(buildCompanyViewConfig(), options?.companyId));
        return res.data.map((i: InteractionApiRecord) => ({
            id: String(i.id || ''),
            type: i.type || '',
            content: i.content || '',
            date: i.created_at || '',
            author: i.author_name || '',
            channel: i.channel,
            result: i.result,
            metadata: i.metadata || i.metadata_jsonb || null
        }));
    },

    createInteraction: async (leadId: string, type: string, content: string, extra?: { channel?: string, result?: string, metadata?: unknown }, options?: { companyId?: string | null }): Promise<Interaction> => {
        const res = await api.post(`/api/crm/mailing/leads/${leadId}/interactions`, {
            type, content,
            channel: extra?.channel,
            result: extra?.result,
            metadata: extra?.metadata
        }, buildManagedCompanyConfig({}, options?.companyId));
        const i = res.data as InteractionApiRecord;
        return {
            id: String(i.id || ''),
            type: i.type || type,
            content: i.content || content,
            date: i.created_at || '',
            author: i.author_name || '',
            channel: i.channel,
            result: i.result,
            metadata: i.metadata
        };
    },

    getLeadSummary: async (leadId: string, options?: { companyId?: string | null }): Promise<{
        status: string;
        lastContact: string | null;
        attempts: number;
        nextFollowUp: string | null;
        campaign: string | null;
        agentId: string | null;
        marketingCampaignId?: number | null;
        marketingCampaignName?: string | null;
        marketingCampaignStatus?: string | null;
        marketingDeliveryStatus?: string | null;
        marketingLastSentAt?: string | null;
        manualMessageLastSentAt?: string | null;
        manualMessageSenderName?: string | null;
        manualMessageStatus?: string | null;
    }> => {
        const res = await api.get(`/api/crm/mailing/leads/${leadId}/summary`, buildManagedCompanyConfig(buildCompanyViewConfig(), options?.companyId));
        return res.data;
    },

    getCampaignAgentStats: async (campaignName: string): Promise<ApiJsonRecord> => {
        const res = await api.get('/api/crm/mailing/campaigns/agent-stats', buildCompanyViewConfig({
            params: { name: campaignName }
        }));
        return res.data;
    },

    getUnlinkedCommissions: async (): Promise<ApiJsonList> => {
        const res = await api.get('/api/crm/settings/unlinked-commissions', buildCompanyViewConfig());
        return res.data;
    },
    createUnlinkedCommissionsBulk: async (items: ApiJsonList): Promise<{ count: number }> => {
        const res = await api.post('/api/crm/settings/unlinked-commissions/bulk', { items });
        return res.data;
    },
    deleteAllUnlinkedCommissions: async () => {
        const res = await api.delete('/api/crm/settings/unlinked-commissions/all');
        return res.data;
    },
    cleanupUnlinkedCommissionDuplicates: async (): Promise<{ deleted: number }> => {
        const res = await api.post('/api/crm/settings/unlinked-commissions/cleanup-duplicates');
        return res.data;
    },

    getPortabilityRequests: async (): Promise<ApiJsonList> => {
        const res = await api.get('/api/crm/settings/portability-requests', buildCompanyViewConfig());
        return res.data;
    },
    updatePortabilityRequest: async (id: string, data: Record<string, unknown>) => {
        const res = await api.put(`/api/crm/settings/portability-requests/${id}`, data);
        return res.data;
    },

    getImportBatches: async (): Promise<ApiJsonList> => {
        const res = await api.get('/api/crm/settings/import-batches');
        return res.data;
    },
    createImportBatch: async (data: { id?: string; fileName: string; count: number }) => {
        const res = await api.post('/api/crm/settings/import-batches', data);
        return res.data;
    },
    deleteImportBatch: async (id: string) => {
        const res = await api.delete(`/api/crm/settings/import-batches/${id}`);
        return res.data;
    },

    // Banks
    createBank: async (name: string): Promise<Bank> => {
        const res = await api.post<Bank>('/api/crm/settings/banks', { name });
        return res.data;
    },
    updateBank: async (id: string, name: string): Promise<Bank> => {
        const res = await api.put<Bank>(`/api/crm/settings/banks/${id}`, { name });
        return res.data;
    },
    deleteBank: async (id: string): Promise<void> => {
        await api.delete(`/api/crm/settings/banks/${id}`);
    },

    // Promoters
    createPromoter: async (name: string): Promise<Promoter> => {
        const res = await api.post<Promoter>('/api/crm/settings/promoters', { name });
        return res.data;
    },
    updatePromoter: async (id: string, name: string): Promise<Promoter> => {
        const res = await api.put<Promoter>(`/api/crm/settings/promoters/${id}`, { name });
        return res.data;
    },
    deletePromoter: async (id: string): Promise<void> => {
        await api.delete(`/api/crm/settings/promoters/${id}`);
    },

    // --- Segments (Aux) ---
    createSegment: async (name: string): Promise<Segment> => {
        const res = await api.post<Segment>('/api/crm/settings/segments', { name });
        return res.data;
    },
    toggleSegment: async (id: string): Promise<Segment> => {
        const res = await api.put<Segment>(`/api/crm/settings/segments/${id}/toggle`);
        return res.data;
    },
    deleteSegment: async (id: string): Promise<void> => {
        await api.delete(`/api/crm/settings/segments/${id}`);
    },

    // --- Products (Aux) ---
    createProduct: async (name: string): Promise<Product> => {
        const res = await api.post<Product>('/api/crm/settings/products', { name });
        return res.data;
    },
    toggleProduct: async (id: string): Promise<Product> => {
        const res = await api.put<Product>(`/api/crm/settings/products/${id}/toggle`);
        return res.data;
    },
    deleteProduct: async (id: string): Promise<void> => {
        await api.delete(`/api/crm/settings/products/${id}`);
    },

    // --- SUPPORT TICKETS ---
    getTickets: async (): Promise<Ticket[]> => {
        const res = await api.get<SupportTicketApiRecord[]>('/api/crm/support/tickets');
        return res.data.map(t => ({
            id: String(t.id || ''),
            subject: t.subject || '',
            status: t.status || '',
            priority: t.priority || '',
            createdAt: t.created_at || '',
            updatedAt: t.updated_at,
            companyId: t.company_id ? String(t.company_id) : undefined,
            userId: t.user_id ? String(t.user_id) : undefined,
            userName: t.user_name || t.user_email,
            category: t.category_name || t.category,
            companyName: t.company_name,
            description: t.description,
            slaDeadline: t.sla_deadline,
            assigneeId: t.assignee_id,
        }));
    },

    createTicket: async (data: { subject: string; priority: string; initialMessage: string; categoria?: string; category_id?: string }): Promise<Ticket> => {
        const res = await api.post<SupportTicketApiRecord>('/api/crm/support/tickets', data);
        const t = res.data;
        return {
            id: String(t.id || ''),
            subject: t.subject || data.subject,
            status: t.status || '',
            priority: t.priority || data.priority,
            createdAt: t.created_at || '',
            companyId: t.company_id ? String(t.company_id) : undefined,
            userId: t.user_id ? String(t.user_id) : undefined,
            slaDeadline: t.sla_deadline,
        };
    },

    // --- SUPPORT CATEGORIES ---
    getCategories: async () => {
        const res = await api.get('/api/crm/support/categories');
        return res.data;
    },

    getAllCategories: async () => {
        const res = await api.get('/api/crm/support/categories/all');
        return res.data;
    },

    createCategory: async (data: { name: string; description?: string; priority: string; sla_hours: number; sort_order?: number }) => {
        const res = await api.post('/api/crm/support/categories', data);
        return res.data;
    },

    updateCategory: async (id: string, data: ApiJsonRecord) => {
        const res = await api.put(`/api/crm/support/categories/${id}`, data);
        return res.data;
    },

    deleteCategory: async (id: string) => {
        await api.delete(`/api/crm/support/categories/${id}`);
    },

    getTicketMessages: async (ticketId: string): Promise<TicketMessage[]> => {
        const res = await api.get(`/api/crm/support/tickets/${ticketId}/messages`);
        // Backend returns mixed flags (is_admin_reply) -> map to semantic 'autor' if needed by UI
        // But UI seems to use 'autor' property. Backend route (line 1124) maps it!
        // "autor: row.is_admin_reply ? 'support' : 'user'"
        return res.data;
    },

    replyTicket: async (ticketId: string, message: string, attachmentUrl?: string, attachmentName?: string): Promise<TicketMessage> => {
        const res = await api.post(`/api/crm/support/tickets/${ticketId}/messages`, { message, attachmentUrl, attachmentName });
        return res.data;
    },

    updateTicketStatus: async (ticketId: string, status: string): Promise<Ticket> => {
        const res = await api.put<SupportTicketApiRecord>(`/api/crm/support/tickets/${ticketId}/status`, { status });
        const t = res.data;
        return {
            id: String(t.id || ''),
            subject: t.subject || '',
            status: t.status || '',
            priority: t.priority || '',
            createdAt: t.created_at || '',
            updatedAt: t.updated_at,
            companyId: t.company_id ? String(t.company_id) : undefined,
            userId: t.user_id ? String(t.user_id) : undefined
        };
    },

    // --- ARQUIVOS (POSTGRESQL) ---
    uploadFile: async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append('file', file);

        const res = await api.post<{ url: string }>('/api/crm/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return res.data.url;
    },

    uploadFileRecord: async (file: File): Promise<{ id: string; url: string; name: string; type: string }> => {
        const formData = new FormData();
        formData.append('file', file);

        const res = await api.post<{ id: string; url: string; name: string; type: string }>('/api/crm/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        return res.data;
    },

    linkWhatsAppTicketContext: async (ticketId: string, payload: { client_id?: string; deal_id?: string | null }) => {
        const res = await api.put(`/api/crm/chat/tickets/${ticketId}/context`, payload);
        return res.data;
    },
    sendWhatsAppTicketMessage: async (ticketId: string, payload: { content: string; scheduledFor?: string | null; signatureEnabled?: boolean }) => {
        const res = await api.post(`/api/crm/chat/tickets/${ticketId}/send`, payload);
        return res.data;
    },

    getWhatsAppInboxOptions: async (): Promise<{ queues: ApiJsonList; tags: ApiJsonList; phases: string[] }> => {
        const res = await api.get('/api/crm/chat/options');
        return res.data;
    },

    getWhatsAppOnlineAgents: async (): Promise<WhatsAppOnlineAgent[]> => {
        const res = await api.get('/api/crm/chat/online-agents');
        if (!Array.isArray(res.data)) {
            return [];
        }

        return res.data.map((item: WhatsAppOnlineAgentApiRecord) => ({
            id: String(item?.user_id || item?.id || '').trim(),
            name: item?.name || 'Usuário',
            active_sessions: Number(item?.active_sessions || 0),
            last_seen_at: item?.last_seen_at || null,
            is_online: true
        })).filter((item: WhatsAppOnlineAgent) => item.id);
    },

    getWhatsAppTeams: async () => {
        const res = await api.get('/api/wa/admin/teams');
        return res.data;
    },

    createWhatsAppTeam: async (payload: {
        name: string;
        color?: string;
        isActive?: boolean;
        userIds?: string[];
    }) => {
        const res = await api.post('/api/wa/admin/teams', payload);
        return res.data;
    },

    updateWhatsAppTeam: async (teamId: number, payload: {
        name?: string;
        color?: string;
        isActive?: boolean;
        userIds?: string[];
    }) => {
        const res = await api.put(`/api/wa/admin/teams/${teamId}`, payload);
        return res.data;
    },

    deleteWhatsAppTeam: async (teamId: number) => {
        const res = await api.delete(`/api/wa/admin/teams/${teamId}`);
        return res.data;
    },

    getWhatsAppRoutingRules: async () => {
        const res = await api.get('/api/wa/admin/routing-rules');
        return res.data;
    },

    createWhatsAppRoutingRule: async (payload: {
        name: string;
        priority?: number;
        isActive?: boolean;
        queueId?: number | null;
        teamId?: number | null;
        targetPhase?: string | null;
        keywordMatch?: string | null;
        phonePrefix?: string | null;
        productTag?: string | null;
    }) => {
        const res = await api.post('/api/wa/admin/routing-rules', payload);
        return res.data;
    },

    updateWhatsAppRoutingRule: async (ruleId: number, payload: {
        name?: string;
        priority?: number;
        isActive?: boolean;
        queueId?: number | null;
        teamId?: number | null;
        targetPhase?: string | null;
        keywordMatch?: string | null;
        phonePrefix?: string | null;
        productTag?: string | null;
    }) => {
        const res = await api.put(`/api/wa/admin/routing-rules/${ruleId}`, payload);
        return res.data;
    },

    deleteWhatsAppRoutingRule: async (ruleId: number) => {
        const res = await api.delete(`/api/wa/admin/routing-rules/${ruleId}`);
        return res.data;
    },

    getWhatsAppSupervisorDashboard: async () => {
        const res = await api.get('/api/wa/admin/supervisor-dashboard');
        return res.data;
    },

    getWhatsAppTicketPreferences: async (ticketId: string): Promise<ApiJsonRecord> => {
        const res = await api.get(`/api/crm/chat/tickets/${ticketId}/preferences`);
        return res.data;
    },

    getWhatsAppInboxPreferences: async (): Promise<WhatsAppInboxPreferences> => {
        const res = await api.get('/api/crm/chat/inbox/preferences');
        return res.data;
    },

    saveWhatsAppInboxPreferences: async (payload: Partial<WhatsAppInboxPreferences>): Promise<WhatsAppInboxPreferences> => {
        const res = await api.put('/api/crm/chat/inbox/preferences', payload);
        return res.data;
    },

    createWhatsAppInboxView: async (payload: {
        name: string;
        inboxMode?: WhatsAppInboxMode;
        activeTab: WhatsAppInboxTab;
        queueFilterId?: string;
        ticketSearchTerm?: string;
    }): Promise<WhatsAppInboxPreferences> => {
        const res = await api.post('/api/crm/chat/inbox/views', payload);
        return res.data;
    },

    updateWhatsAppInboxView: async (viewId: string, payload: Partial<WhatsAppInboxSavedView>): Promise<WhatsAppInboxPreferences> => {
        const res = await api.put(`/api/crm/chat/inbox/views/${viewId}`, payload);
        return res.data;
    },

    deleteWhatsAppInboxView: async (viewId: string): Promise<WhatsAppInboxPreferences> => {
        const res = await api.delete(`/api/crm/chat/inbox/views/${viewId}`);
        return res.data;
    },

    updateWhatsAppTicketPreferences: async (ticketId: string, payload: { marketing_status: 'allowed' | 'blocked' | 'wrong_number'; blocked_reason?: string }) => {
        const res = await api.put(`/api/crm/chat/tickets/${ticketId}/preferences`, payload);
        return res.data;
    },

    updateWhatsAppTicketMeta: async (ticketId: string, payload: { queue_id?: number | null; phase?: string; tag_ids?: number[] }) => {
        const res = await api.put(`/api/crm/chat/tickets/${ticketId}/meta`, payload);
        return res.data;
    },

    updateWhatsAppTicketSignature: async (ticketId: string, enabled: boolean) => {
        const res = await api.put(`/api/crm/chat/tickets/${ticketId}/signature`, { enabled });
        return res.data;
    },

    validateCampaignRecipients: async (payload: { contacts?: Array<Record<string, unknown>>; source?: string; mailingCampaignId?: string; mailingFilterPreset?: string; variableMapping?: Record<string, string>; mappingMode?: Record<string, string>; staticMapping?: Record<string, string>; }) => {
        const res = await api.post('/api/campaigns/validate-recipients', payload);
        return res.data;
    },
    listCampaignAudiencePresets: async () => {
        const res = await api.get('/api/campaign-audience-presets');
        return res.data;
    },
    saveCampaignAudiencePreset: async (payload: {
        name?: string;
        source: 'crm' | 'mailing';
        filters: {
            searchTerm?: string;
            segment?: string;
            productTag?: string;
            orgao?: string;
            governmentScope?: 'all' | 'government' | 'prefeitura';
            selectedIds?: string[];
            mailingCampaignId?: string;
            mailingCampaignName?: string;
            mailingFilterPreset?: string;
        };
    }) => {
        const res = await api.post('/api/campaign-audience-presets', payload);
        return res.data;
    },
    deleteCampaignAudiencePreset: async (presetId: string) => {
        const res = await api.delete(`/api/campaign-audience-presets/${presetId}`);
        return res.data;
    },
    getCampaignFailures: async (campaignId: number | string, params?: { limit?: number; offset?: number }) => {
        const res = await api.get(`/api/campaigns/${campaignId}/failures`, { params });
        return res.data;
    },
    getCampaignDetails: async (campaignId: number | string) => {
        const res = await api.get(`/api/campaigns/${campaignId}/details`);
        return res.data;
    },
    getCampaignResultItems: async (
        campaignId: number | string,
        params?: {
            group?: 'messages' | 'preference';
            status?: 'all' | 'sent' | 'delivered' | 'read' | 'failed' | 'pending' | 'excluded' | 'override';
            limit?: number;
            offset?: number;
        }
    ) => {
        const res = await api.get(`/api/campaigns/${campaignId}/results`, { params });
        return res.data;
    },
    retryCampaignFailures: async (campaignId: number | string) => {
        const res = await api.post(`/api/campaigns/${campaignId}/retry-failures`);
        return res.data;
    },
    duplicateCampaign: async (campaignId: number | string) => {
        const res = await api.post(`/api/campaigns/${campaignId}/duplicate`);
        return res.data;
    },
    exportCampaignResults: async (campaignId: number | string) => {
        const res = await api.get(`/api/campaigns/${campaignId}/results-export`, {
            responseType: 'blob'
        });
        return {
            blob: res.data,
            contentDisposition: res.headers['content-disposition'] as string | undefined
        };
    },

    // --- MENSAGENS RÁPIDAS (QUICK MESSAGES) ---
    getWhatsAppQuickMessages: async (queueId?: number) => {
        const params = queueId ? { queue_id: queueId } : {};
        const res = await api.get('/api/crm/chat/quick-messages', { params });
        return res.data;
    },

    createWhatsAppQuickMessage: async (payload: { title: string; message: string; queue_id?: number | null; is_personal?: boolean }) => {
        const res = await api.post('/api/crm/chat/quick-messages', payload);
        return res.data;
    },

    updateWhatsAppQuickMessage: async (id: number, payload: { title?: string; message?: string; queue_id?: number | null; is_active?: boolean }) => {
        const res = await api.put(`/api/crm/chat/quick-messages/${id}`, payload);
        return res.data;
    },

    deleteWhatsAppQuickMessage: async (id: number) => {
        const res = await api.delete(`/api/crm/chat/quick-messages/${id}`);
        return res.data;
    },

    getWhatsAppMediaLibrary: async (params?: { includeInactive?: boolean; queueId?: number | null; category?: string | null }): Promise<WhatsAppMediaLibraryItem[]> => {
        const res = await api.get('/api/crm/chat/media-library', {
            params: {
                include_inactive: params?.includeInactive ? 'true' : undefined,
                queue_id: params?.queueId || undefined,
                category: params?.category || undefined
            }
        });
        return Array.isArray(res.data) ? res.data : [];
    },

    createWhatsAppMediaLibraryItem: async (payload: {
        file_id: string;
        title: string;
        description?: string;
        category?: string;
        queue_id?: number | null;
        is_active?: boolean;
        sort_order?: number;
    }): Promise<WhatsAppMediaLibraryItem> => {
        const res = await api.post('/api/crm/chat/media-library', payload);
        return res.data;
    },

    updateWhatsAppMediaLibraryItem: async (id: string, payload: {
        file_id?: string;
        title?: string;
        description?: string;
        category?: string;
        queue_id?: number | null;
        is_active?: boolean;
        sort_order?: number;
    }): Promise<WhatsAppMediaLibraryItem> => {
        const res = await api.put(`/api/crm/chat/media-library/${id}`, payload);
        return res.data;
    },

    deleteWhatsAppMediaLibraryItem: async (id: string) => {
        const res = await api.delete(`/api/crm/chat/media-library/${id}`);
        return res.data;
    },

    getWhatsAppConversationMedia: async (params?: { limit?: number; direction?: 'inbound' | 'outbound' | null }): Promise<WhatsAppConversationMediaItem[]> => {
        const res = await api.get('/api/crm/chat/conversation-files', {
            params: {
                limit: params?.limit || undefined,
                direction: params?.direction || undefined
            }
        });
        return Array.isArray(res.data) ? res.data : [];
    },

    applyWhatsAppCorbanTriagePreset: async (): Promise<ApiJsonRecord> => {
        const res = await api.post('/api/wa/admin/triage-presets/corban');
        return res.data;
    },

    // --- FILA DE AUDITORIA E ANEXOS DA VENDA ---
    getDealAttachments: async (dealId: string): Promise<ApiJsonList> => {
        const res = await api.get(`/api/crm/deals/${dealId}/attachments`);
        return res.data;
    },
    addDealAttachment: async (dealId: string, payload: { file_url: string, file_name: string }): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/deals/${dealId}/attachments`, payload);
        return res.data;
    },
    deleteDealAttachment: async (dealId: string, attachmentId: string): Promise<ApiJsonRecord> => {
        const res = await api.delete(`/api/crm/deals/${dealId}/attachments/${attachmentId}`);
        return res.data;
    },

    getAuditQueue: async (filters?: { storeId?: string | null }): Promise<Deal[]> => {
        const res = await api.get('/api/crm/audit/queue', buildCompanyViewConfig({
            params: {
                ...(filters?.storeId ? { storeId: filters.storeId } : {})
            }
        }));
        return res.data;
    },
    submitToAudit: async (dealId: string): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/audit/deals/${dealId}/submit`);
        return res.data;
    },
    assignAudit: async (dealId: string): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/audit/deals/${dealId}/assign`);
        return res.data;
    },
    reviewAudit: async (dealId: string, decision: string, notes: string): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/audit/deals/${dealId}/review`, { decision, notes });
        return res.data;
    },
    addAuditNote: async (dealId: string, notes: string): Promise<ApiJsonRecord> => {
        const res = await api.post(`/api/crm/audit/deals/${dealId}/note`, { notes });
        return res.data;
    },
    getAuditHistory: async (dealId: string): Promise<ApiJsonList> => {
        const res = await api.get(`/api/crm/audit/deals/${dealId}/history`);
        return res.data;
    },
    resetDefaults: async (): Promise<ApiJsonRecord> => {
        const res = await api.post('/api/crm/settings/reset-defaults');
        return res.data;
    },

    // --- BANK ACCESS REQUESTS ---
    createBankAccessRequest: async (data: {
        bankId: string;
        bankName?: string;
        name: string;
        email: string;
        cpf: string;
        rg?: string;
        birthDate?: string;
        phone: string;
        motherName?: string;
        fatherName?: string;
        cep?: string;
        address?: string;
        number?: string;
        neighborhood?: string;
        city?: string;
        uf?: string;
        febrabanExpiry?: string;
        observation?: string;
    }): Promise<ApiJsonRecord> => {
        const res = await api.post('/api/crm/bank-access-requests', data);
        return res.data;
    },
    getBankAccessRequests: async (): Promise<ApiJsonList> => {
        const res = await api.get('/api/crm/bank-access-requests');
        return res.data;
    },
    updateBankAccessRequest: async (id: string, data: {
        status?: string;
        admin_notes?: string;
        credentials?: { username?: string; password?: string };
    }): Promise<ApiJsonRecord> => {
        const res = await api.put(`/api/crm/bank-access-requests/${id}`, data);
        return res.data;
    },
    getBankCredentials: async (): Promise<ApiJsonList> => {
        const res = await api.get('/api/crm/bank-access-requests/credentials');
        return res.data;
    },
    getBankCredentialDirectory: async (): Promise<Array<{ id: string; name: string }>> => {
        const res = await api.get('/api/crm/bank-access-requests/credentials/directory');
        return res.data;
    },
    createBankCredential: async (data: {
        user_id: string;
        bank_id?: string;
        bank_name?: string;
        username: string;
        password: string;
        notes?: string;
    }): Promise<ApiJsonRecord> => {
        const res = await api.post('/api/crm/bank-access-requests/credentials', data);
        return res.data;
    },
    updateBankCredential: async (id: string, data: {
        user_id?: string;
        bank_id?: string;
        bank_name?: string;
        username?: string;
        password?: string;
        notes?: string;
    }): Promise<ApiJsonRecord> => {
        const res = await api.put(`/api/crm/bank-access-requests/credentials/${id}`, data);
        return res.data;
    }
};

export default api;
